import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
    X, Send, Sparkles, Bot, User, FileText, Brain, CheckSquare, BookOpen,
    Save, Clock, Loader, ChevronRight, ChevronLeft, Lightbulb, RefreshCw, Trash2,
    Settings, Users, Calendar, Paperclip, AlertCircle, Check, Upload, Globe, File,
    GripVertical, Edit3, ChevronDown, ChevronUp, ExternalLink, Shield, Layers, Loader2, StopCircle,
    PanelLeftClose, PanelLeftOpen, MessageSquare, Eye, Search, Hash, Video,
    Minus, Info, Heading, PenTool, FormInput, ToggleLeft, Type, MousePointer, ArrowLeft
} from 'lucide-react';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import toast from 'react-hot-toast';
import api from '../../utils/api';
import QuizBuilder from './QuizBuilder';
import TrainingBuilder from './TrainingBuilder';
import ChecklistBuilder from './ChecklistBuilder';
import CombinedTaskBuilder from './CombinedTaskBuilder';
import EmployeeSelector from './EmployeeSelector';
import UnifiedFilePickerModal from '../Common/UnifiedFilePickerModal';
import AdminTaskPreviewModal from './AdminTaskPreviewModal';
import AssignmentPlaceholderPicker from '../Courses/AssignmentPlaceholderPicker';
import DocumentPromptModal from './DocumentPromptModal';

// Web Search Results Display Component - Collapsible cards with View More
const WebSearchResultsDisplay = ({ results }) => {
    const [expandedIndex, setExpandedIndex] = useState(null);

    if (!results || results.length === 0) return null;

    return (
        <div className="mt-3 pt-3 border-t border-gray-300/50 dark:border-slate-700/30">
            <div className="flex items-center gap-2 mb-2">
                <Globe className="w-4 h-4 text-blue-500 dark:text-blue-400" />
                <span className="text-xs font-medium text-gray-500 dark:text-slate-400">Web Sources</span>
            </div>
            <div className="space-y-2">
                {results.map((result, index) => (
                    <div
                        key={index}
                        className="bg-gray-200/60 dark:bg-slate-800/40 border border-gray-300/60 dark:border-slate-700/50 rounded-lg overflow-hidden"
                    >
                        {/* Header - Always visible */}
                        <div className="px-3 py-2">
                            <div className="flex items-start justify-between gap-2">
                                <div className="flex-1 min-w-0">
                                    <a
                                        href={result.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-500 dark:hover:text-blue-300 truncate block"
                                    >
                                        {result.title}
                                    </a>
                                    <span className="text-xs text-gray-500 dark:text-slate-500">{result.domain}</span>
                                </div>
                                <a
                                    href={result.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="p-1 text-gray-400 dark:text-slate-500 hover:text-blue-500 dark:hover:text-blue-400 transition-colors flex-shrink-0"
                                >
                                    <ExternalLink className="w-3.5 h-3.5" />
                                </a>
                            </div>
                            {/* Preview snippet - 2-3 lines */}
                            <p className="text-xs text-gray-600 dark:text-slate-400 mt-1 line-clamp-2">
                                {result.snippet}
                            </p>
                        </div>
                        {/* Expandable full content */}
                        {result.fullContent && (
                            <>
                                {expandedIndex === index && (
                                    <div className="px-3 pb-2">
                                        <div className="text-xs text-gray-700 dark:text-slate-300 bg-gray-100 dark:bg-slate-900/50 p-2 rounded max-h-40 overflow-y-auto">
                                            {result.fullContent}
                                        </div>
                                    </div>
                                )}
                                <button
                                    onClick={() => setExpandedIndex(expandedIndex === index ? null : index)}
                                    className="w-full flex items-center justify-center gap-1 px-3 py-1.5 text-xs text-gray-500 dark:text-slate-400 hover:text-gray-700 dark:hover:text-slate-300 bg-gray-200/60 dark:bg-slate-800/60 hover:bg-gray-300/50 dark:hover:bg-slate-700/50 transition-colors"
                                >
                                    {expandedIndex === index ? (
                                        <>
                                            <ChevronUp className="w-3 h-3" />
                                            <span>Show Less</span>
                                        </>
                                    ) : (
                                        <>
                                            <ChevronDown className="w-3 h-3" />
                                            <span>View More</span>
                                        </>
                                    )}
                                </button>
                            </>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
};

// Sortable Block Card component for drag-and-drop
const SortableBlockCard = ({ section, index, isSelected, onSelect, onDelete, getBlockIcon, getBlockColor, getBlockBgColor, getSectionSummary, sectionsLength }) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: section.id || `section-${index}` });
    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        zIndex: isDragging ? 50 : undefined
    };
    const BlockIcon = getBlockIcon(section.section_type);

    // Check if section has content (for incomplete badge)
    const isEmpty = (() => {
        switch (section.section_type) {
            case 'quiz': return !section.content?.questions?.length;
            case 'training': return !section.content?.sections?.length;
            case 'checklist': return !section.content?.items?.length;
            case 'form_fields': return !section.content?.fields?.length;
            case 'content': return !section.content || (typeof section.content === 'string' && !section.content.trim());
            case 'video': return !section.content?.url;
            case 'heading': return !section.content?.text;
            case 'callout': return !section.content?.message;
            default: return false;
        }
    })();

    return (
        <div
            ref={setNodeRef}
            style={style}
            onClick={() => onSelect(index)}
            className={`flex items-center gap-3 px-4 py-3.5 rounded-xl border-2 cursor-pointer transition-all group ${isSelected
                ? 'border-blue-400 dark:border-blue-500/50 bg-blue-50/60 dark:bg-blue-500/5 shadow-md'
                : 'border-gray-200 dark:border-slate-700/60 bg-white dark:bg-slate-800/60 hover:border-gray-300 dark:hover:border-slate-600 hover:shadow-sm'
                }`}
        >
            {/* Drag Handle */}
            <div
                {...attributes}
                {...listeners}
                className="flex-shrink-0 cursor-grab active:cursor-grabbing p-1 -ml-1 text-gray-300 dark:text-slate-600 hover:text-gray-500 dark:hover:text-slate-400 touch-none"
                onClick={e => e.stopPropagation()}
            >
                <GripVertical className="w-4 h-4" />
            </div>

            {/* Icon */}
            <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${getBlockBgColor(section.section_type)}`}>
                <BlockIcon className={`w-4 h-4 ${getBlockColor(section.section_type)}`} />
            </div>

            {/* Title + Meta */}
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                    <p className="text-sm font-semibold text-gray-800 dark:text-white truncate">{section.title}</p>
                    {isEmpty && section.is_required && (
                        <span className="flex items-center gap-1 text-[10px] font-semibold text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-500/10 px-1.5 py-0.5 rounded-full flex-shrink-0">
                            <AlertCircle className="w-3 h-3" /> Incomplete
                        </span>
                    )}
                </div>
                <p className="text-xs text-gray-400 dark:text-slate-500 mt-0.5">{getSectionSummary(section)}</p>
            </div>

            {/* Delete */}
            <button
                onClick={(e) => { e.stopPropagation(); onDelete(index); }}
                className="p-1.5 text-gray-300 dark:text-slate-600 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10 rounded-lg opacity-0 group-hover:opacity-100 transition-all flex-shrink-0"
            >
                <Trash2 className="w-4 h-4" />
            </button>
        </div>
    );
};

const AITaskGenerator = ({ isOpen, onClose, onSuccess, phase, editTask = null, initialMessages = null, isComplianceMode = false, prefilledData = null }) => {
    // Determine if we're in edit mode
    const isEditMode = editTask !== null && editTask?.id;

    // Chat State
    const [messages, setMessages] = useState([]);
    const [inputMessage, setInputMessage] = useState('');
    const [isGenerating, setIsGenerating] = useState(false);
    const [sessionId, setSessionId] = useState(null);
    const chatEndRef = useRef(null);
    const inputRef = useRef(null);

    // Mobile State - 'chat' or 'editor' panel
    const [mobileActivePanel, setMobileActivePanel] = useState('chat');

    // Chat panel collapse state (desktop only)
    const [isChatCollapsed, setIsChatCollapsed] = useState(() => {
        const saved = localStorage.getItem('ai_task_generator_chat_collapsed');
        return saved === 'true';
    });

    const toggleChatCollapsed = useCallback(() => {
        setIsChatCollapsed(prev => {
            const next = !prev;
            localStorage.setItem('ai_task_generator_chat_collapsed', String(next));
            return next;
        });
    }, []);

    // Split Panel State (Problem 1: Resizable Split)
    const [splitRatio, setSplitRatio] = useState(() => {
        const saved = localStorage.getItem('ai_task_generator_split_ratio');
        return saved ? parseFloat(saved) : 40;
    });
    const [isDragging, setIsDragging] = useState(false);
    const containerRef = useRef(null);
    const MIN_CHAT_WIDTH = 300; // px
    const MIN_EDITOR_WIDTH = 400; // px

    // Content Cache State (Problem 3: Content Persistence)
    const [contentCache, setContentCache] = useState({
        task: '',
        quiz: null,
        training: null,
        checklist: null,
        combined: []
    });

    // Editor Changes Tracking (Problem 2: Bidirectional Sync)
    const [editorChanges, setEditorChanges] = useState([]);

    // Streaming State (Problem 4: Streaming Response)
    const [streamingMessage, setStreamingMessage] = useState('');
    const [streamingPhase, setStreamingPhase] = useState(null);
    const [isStreaming, setIsStreaming] = useState(false);

    // Task Editor State
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        task_type: 'combined',
        content: '',
        phase: phase || 'onboarding',
        category: '',
        priority: 'medium',
        estimated_duration: 30,
        requires_proof: false,
        requires_signature: false,
        assigned_roles: [],
        assigned_employee_ids: [],
        assigned_team_ids: [],
        assignment_metadata: { recursive: false, leaders_only: false },
        assignment_type: 'role',
        passing_score: 70,
        quiz_grading_type: 'auto',
        allow_retakes: false,
        max_attempts: 1,
        due_days_after_assignment: 7,
        scheduled_date: null,
        attachments: [],
        is_compliance_required: isComplianceMode,
        compliance_deadline_days: null,
        is_combined: true,
        sections: [],
        assignment_placeholders: []
    });

    const [isSaving, setIsSaving] = useState(false);
    const [availableRoles, setAvailableRoles] = useState([]);
    const [activeEditorTab, setActiveEditorTab] = useState('content'); // content, settings, assignment

    // 3-panel editor state
    const [selectedSectionIndex, setSelectedSectionIndex] = useState(null);
    const [blockSearchQuery, setBlockSearchQuery] = useState('');
    const [rightPanelTab, setRightPanelTab] = useState('section'); // 'section' or 'task'

    // Block type definitions for the sidebar
    const BLOCK_TYPES = [
        {
            category: 'CONTENT', items: [
                { type: 'content', label: 'Text', desc: 'Rich text content', icon: FileText, color: 'gray' },
                { type: 'heading', label: 'Heading', desc: 'Section header', icon: Heading, color: 'orange' },
                { type: 'video', label: 'Video', desc: 'Embed video link', icon: Video, color: 'red' },
                { type: 'divider', label: 'Divider', desc: 'Visual separator', icon: Minus, color: 'slate' },
                { type: 'callout', label: 'Callout', desc: 'Info, tip, or warning', icon: Info, color: 'cyan' },
            ]
        },
        {
            category: 'INTERACTIVE', items: [
                { type: 'form_fields', label: 'Form', desc: 'Collect employee data', icon: FormInput, color: 'indigo' },
                { type: 'quiz', label: 'Quiz', desc: 'Test understanding', icon: Brain, color: 'green' },
                { type: 'checklist', label: 'Checklist', desc: 'Multi-item checklist', icon: CheckSquare, color: 'amber' },
                { type: 'training', label: 'Training', desc: 'Step-by-step modules', icon: BookOpen, color: 'blue' },
            ]
        },
        {
            category: 'COMPLETION', items: [
                { type: 'signature', label: 'Signature', desc: 'Digital signature', icon: PenTool, color: 'rose' },
                { type: 'proof_upload', label: 'File Upload', desc: 'Request documents', icon: Upload, color: 'purple' },
            ]
        }
    ];

    const getFilteredBlocks = () => {
        if (!blockSearchQuery.trim()) return BLOCK_TYPES;
        const q = blockSearchQuery.toLowerCase();
        return BLOCK_TYPES.map(cat => ({
            ...cat,
            items: cat.items.filter(item => item.label.toLowerCase().includes(q) || item.type.toLowerCase().includes(q))
        })).filter(cat => cat.items.length > 0);
    };

    // DnD Kit sensors and handler
    const dndSensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));
    const handleDragEnd = (event) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;
        const oldIndex = formData.sections.findIndex(s => (s.id || `section-${formData.sections.indexOf(s)}`) === active.id);
        const newIndex = formData.sections.findIndex(s => (s.id || `section-${formData.sections.indexOf(s)}`) === over.id);
        if (oldIndex !== -1 && newIndex !== -1) {
            const reordered = arrayMove(formData.sections, oldIndex, newIndex);
            setFormData(prev => ({ ...prev, sections: reordered }));
            if (selectedSectionIndex === oldIndex) setSelectedSectionIndex(newIndex);
            else if (selectedSectionIndex === newIndex) setSelectedSectionIndex(oldIndex);
        }
    };

    // Step wizard order
    const editorSteps = ['content', 'settings', 'assignment'];
    const currentStepIndex = editorSteps.indexOf(activeEditorTab);
    const isFirstStep = currentStepIndex === 0;
    const isLastStep = currentStepIndex === editorSteps.length - 1;

    const handleNextStep = () => {
        if (!isLastStep) {
            setActiveEditorTab(editorSteps[currentStepIndex + 1]);
        }
    };

    const handlePrevStep = () => {
        if (!isFirstStep) {
            setActiveEditorTab(editorSteps[currentStepIndex - 1]);
        }
    };

    // Suggestions based on context
    const [suggestions, setSuggestions] = useState([]);

    // AI File Upload State
    const [aiUploadedFiles, setAiUploadedFiles] = useState([]);
    const [isFileUploading, setIsFileUploading] = useState(false);
    const [fileUploadProgress, setFileUploadProgress] = useState(0);
    const [fileProcessingMessage, setFileProcessingMessage] = useState(null);
    const [isWebSearchEnabled, setIsWebSearchEnabled] = useState(false);
    const aiFileInputRef = useRef(null);
    // Web search state
    const [isWebSearching, setIsWebSearching] = useState(false);
    const [currentWebSearchResults, setCurrentWebSearchResults] = useState(null);
    const webSearchResultsRef = useRef(null);
    // Files awaiting user intent choice (attach vs analyze)
    const [pendingIntentFiles, setPendingIntentFiles] = useState([]);


    // Auto-save state
    const [autoSaveStatus, setAutoSaveStatus] = useState('idle'); // 'idle' | 'saving' | 'saved' | 'error'
    const autoSaveTimerRef = useRef(null);
    const draftTaskIdRef = useRef(null); // Track server-created draft ID for new tasks
    const autoSaveSavedTimerRef = useRef(null);

    // Unified File Picker State
    const [showFilePicker, setShowFilePicker] = useState(false);
    const [pickerMode, setPickerMode] = useState('chat'); // 'chat' or 'editor'
    const [isPreviewOpen, setIsPreviewOpen] = useState(false);
    const [showDocumentPromptModal, setShowDocumentPromptModal] = useState(false);
    const [pendingDocumentPromptFiles, setPendingDocumentPromptFiles] = useState([]);

    // Check if editor has content (not blank)
    const hasEditorContent = () => {
        return (
            (formData.title && formData.title.trim() !== '') ||
            (formData.description && formData.description.trim() !== '') ||
            (formData.content && formData.content !== '') ||
            (formData.sections && formData.sections.length > 0) ||
            formData.category !== '' ||
            formData.estimated_duration !== 30
        );
    };

    // Initialize session and welcome message
    useEffect(() => {
        if (isOpen) {
            const newSessionId = `session_${Date.now()}`;
            setSessionId(newSessionId);

            // Edit mode: load existing task data and chat history
            if (isEditMode) {
                // Populate form data from existing task
                setFormData({
                    title: editTask.title || '',
                    description: editTask.description || '',
                    task_type: editTask.task_type || 'task',
                    content: editTask.content || '',
                    phase: editTask.phase || phase || 'onboarding',
                    category: editTask.category || '',
                    priority: editTask.priority || 'medium',
                    estimated_duration: editTask.estimated_duration || 30,
                    requires_proof: editTask.requires_proof || false,
                    requires_signature: editTask.requires_signature || false,
                    assigned_roles: editTask.assigned_roles || [],
                    assigned_employee_ids: editTask.assigned_employee_ids || [],
                    assigned_team_ids: editTask.assigned_team_ids || [],
                    assignment_metadata: editTask.assignment_metadata || { recursive: false, leaders_only: false },
                    assignment_type: editTask.assignment_type || 'role',
                    passing_score: editTask.passing_score || 70,
                    quiz_grading_type: editTask.quiz_grading_type || 'auto',
                    allow_retakes: editTask.allow_retakes || false,
                    max_attempts: editTask.max_attempts || 1,
                    due_days_after_assignment: editTask.due_days_after_assignment || 7,
                    scheduled_date: editTask.scheduled_date || null,
                    attachments: editTask.attachments || [],
                    is_combined: editTask.is_combined || false,
                    sections: editTask.sections || [],
                    assignment_placeholders: editTask.assignment_placeholders || []
                });

                // If it's a combined task, we might need to fetch the full details if sections are missing
                // This happens when editing from the list view which doesn't join sections
                if (editTask.is_combined && (!editTask.sections || editTask.sections.length === 0)) {
                    const fetchFullTask = async () => {
                        try {
                            const response = await api.get(`/tasks/${editTask.id}`);
                            if (response.data.success && response.data.task) {
                                const fullTask = response.data.task;
                                setFormData(prev => ({
                                    ...prev,
                                    sections: fullTask.sections || [],
                                    description: fullTask.description || prev.description,
                                    content: fullTask.content || prev.content
                                }));
                            }
                        } catch (err) {
                            console.error("Failed to fetch full task details:", err);
                        }
                    };
                    fetchFullTask();
                }

                // Restore chat history if available
                if (initialMessages && Array.isArray(initialMessages) && initialMessages.length > 0) {
                    setMessages(initialMessages);
                    // Add a system message indicating edit mode
                    setMessages(prev => [...prev, {
                        id: Date.now(),
                        role: 'assistant',
                        content: `👋 Welcome back! I see you're editing **"${editTask.title}"**. You can continue our conversation to make changes to this task. What would you like to modify?`,
                        timestamp: new Date()
                    }]);
                } else {
                    // No chat history, start fresh with edit context
                    setMessages([{
                        id: Date.now(),
                        role: 'assistant',
                        content: `👋 You're editing the task **"${editTask.title}"**. I can help you make changes to this ${editTask.task_type}. What would you like to modify?\n\nYou can ask me to:\n- Add or change content\n- Modify settings\n- Update assignments`,
                        timestamp: new Date()
                    }]);
                }

                setSuggestions([
                    `Add more content to this ${editTask.task_type}`,
                    'Change the title or description',
                    'Update the estimated duration',
                    'Modify assignments'
                ]);

                fetchRoles();
                return;
            }

            // Check for saved draft session (only for new tasks)
            const savedSession = localStorage.getItem('ai_task_draft_session');
            if (savedSession) {
                try {
                    const parsed = JSON.parse(savedSession);
                    if (parsed.phase === phase) {
                        setMessages(parsed.messages || []);
                        setFormData(parsed.formData || formData);
                        setSuggestions(parsed.suggestions || []);
                        toast.success('Restored your previous draft session');
                        fetchRoles();
                        return;
                    }
                } catch (e) {
                    console.error('Failed to restore session:', e);
                }
            }

            // Helper to build context string for the AI from prefilled data
            const buildPrefilledContextString = (data) => {
                if (!data || Object.keys(data).length === 0) return null;
                const parts = [];
                if (data.title) parts.push(`Title: "${data.title}"`);
                if (data.description) parts.push(`Description: ${data.description}`);
                if (data.category) parts.push(`Category: ${data.category}`);
                if (data.priority) parts.push(`Priority: ${data.priority}`);
                if (data.task_type) parts.push(`Type: ${data.task_type}`);
                return parts.length > 0 ? `Please help me create this task:\n${parts.join('\n')}` : null;
            };

            const prefilledMessageContext = buildPrefilledContextString(prefilledData);

            // Fresh session - add welcome message
            const initialChatMessages = [{
                id: 1,
                role: 'assistant',
                content: `👋 Hello! I'm your AI Task Assistant. I can help you create **tasks, quizzes, training modules, and checklists** for your ${phase} process.\n\nTell me what you'd like to create. For example:\n- "Create a safety compliance quiz with 5 questions"\n- "Build a first-day checklist for new developers"\n- "Generate training content about using our project management tools"`,
                timestamp: new Date()
            }];

            if (prefilledMessageContext) {
                initialChatMessages.push({
                    id: 2,
                    role: 'user',
                    content: prefilledMessageContext,
                    timestamp: new Date()
                });
                initialChatMessages.push({
                    id: 3,
                    role: 'assistant',
                    content: "I've loaded the details you requested. I can refine this task for you, or we can just save it as is. Would you like me to expand on the description or add any specific sections like a quiz or checklist?",
                    timestamp: new Date()
                });
            }

            setMessages(initialChatMessages);

            // FIX: Reset form data to ensure correct phase is used for new tasks
            setFormData({
                title: prefilledData?.title || '',
                description: prefilledData?.description || '',
                task_type: prefilledData?.task_type || 'task',
                content: '',
                phase: prefilledData?.phase || phase || 'onboarding', // Use the current prop!
                category: prefilledData?.category || '',
                priority: prefilledData?.priority || 'medium',
                estimated_duration: 30,
                requires_proof: false,
                requires_signature: false,
                assigned_roles: [],
                assigned_employee_ids: [],
                assigned_team_ids: [],
                assignment_metadata: { recursive: false, leaders_only: false },
                assignment_type: 'role',
                passing_score: 70,
                quiz_grading_type: 'auto',
                allow_retakes: false,
                max_attempts: 1,
                due_days_after_assignment: 7,
                scheduled_date: null,
                attachments: [],
                is_compliance_required: isComplianceMode,
                compliance_deadline_days: null,
                is_combined: false,
                sections: [],
                assignment_placeholders: []
            });

            // Reset other state
            setAiUploadedFiles([]);
            setContentCache({ task: '', quiz: null, training: null, checklist: null, combined: [] });
            setEditorChanges([]);
            setSuggestions([
                `Create a ${phase} orientation quiz`,
                `Build a first-week checklist`,
                `Generate IT setup training`,
                `Create a company policy acknowledgment task`
            ]);

            fetchRoles();
        }
    }, [isOpen, phase, isEditMode, editTask, initialMessages]);

    // Auto-save: performAutoSave callback
    const performAutoSave = useCallback(async () => {
        if (!formData.title?.trim() || isStreaming || isGenerating) return;

        setAutoSaveStatus('saving');
        try {
            const taskPayload = {
                ...formData,
                status: 'draft',
                is_ai_generated: true,
                ai_chat_history: messages
            };

            if (isEditMode) {
                await api.put(`/tasks/${editTask.id}`, taskPayload);
            } else if (draftTaskIdRef.current) {
                await api.put(`/tasks/${draftTaskIdRef.current}`, taskPayload);
            } else {
                const response = await api.post('/tasks', taskPayload);
                if (response.data?.task?.id) {
                    draftTaskIdRef.current = response.data.task.id;
                }
            }

            setAutoSaveStatus('saved');
            // Reset to idle after 2.5s
            if (autoSaveSavedTimerRef.current) clearTimeout(autoSaveSavedTimerRef.current);
            autoSaveSavedTimerRef.current = setTimeout(() => setAutoSaveStatus('idle'), 2500);
        } catch (error) {
            console.error('Auto-save error:', error);
            setAutoSaveStatus('error');
            if (autoSaveSavedTimerRef.current) clearTimeout(autoSaveSavedTimerRef.current);
            autoSaveSavedTimerRef.current = setTimeout(() => setAutoSaveStatus('idle'), 4000);
        }
    }, [formData, messages, isEditMode, editTask, isStreaming, isGenerating]);

    // Auto-save: trigger on formData changes (debounced 2s)
    useEffect(() => {
        if (!isOpen || !formData.title?.trim()) return;

        if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
        autoSaveTimerRef.current = setTimeout(() => {
            performAutoSave();
        }, 2000);

        return () => {
            if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
        };
    }, [formData, isOpen, performAutoSave]);

    // Cleanup auto-save timers on unmount/close
    useEffect(() => {
        return () => {
            if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
            if (autoSaveSavedTimerRef.current) clearTimeout(autoSaveSavedTimerRef.current);
        };
    }, []);

    // Auto-scroll chat
    useEffect(() => {
        chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    // Focus input on open
    useEffect(() => {
        if (isOpen) {
            setTimeout(() => inputRef.current?.focus(), 100);
        }
    }, [isOpen]);

    // Lock body scroll when modal is open (prevents background scrolling on mobile)
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
            document.body.style.position = 'fixed';
            document.body.style.width = '100%';
            document.body.style.top = `-${window.scrollY}px`;
        } else {
            const scrollY = document.body.style.top;
            document.body.style.overflow = '';
            document.body.style.position = '';
            document.body.style.width = '';
            document.body.style.top = '';
            window.scrollTo(0, parseInt(scrollY || '0') * -1);
        }

        return () => {
            document.body.style.overflow = '';
            document.body.style.position = '';
            document.body.style.width = '';
            document.body.style.top = '';
        };
    }, [isOpen]);

    // Drag handlers for resizable split panel (Problem 1)
    const handleMouseDown = useCallback((e) => {
        e.preventDefault();
        setIsDragging(true);
    }, []);

    const handleMouseMove = useCallback((e) => {
        if (!isDragging || !containerRef.current) return;

        const containerRect = containerRef.current.getBoundingClientRect();
        const containerWidth = containerRect.width;
        const mouseX = e.clientX - containerRect.left;

        // Calculate percentage
        let newRatio = (mouseX / containerWidth) * 100;

        // Enforce minimum widths
        const minChatRatio = (MIN_CHAT_WIDTH / containerWidth) * 100;
        const maxChatRatio = 100 - (MIN_EDITOR_WIDTH / containerWidth) * 100;

        newRatio = Math.max(minChatRatio, Math.min(maxChatRatio, newRatio));
        setSplitRatio(newRatio);
    }, [isDragging]);

    const handleMouseUp = useCallback(() => {
        if (isDragging) {
            setIsDragging(false);
            localStorage.setItem('ai_task_generator_split_ratio', splitRatio.toString());
        }
    }, [isDragging, splitRatio]);

    // Add/remove mouse event listeners for dragging
    useEffect(() => {
        if (isDragging) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
            document.body.style.cursor = 'col-resize';
            document.body.style.userSelect = 'none';
        } else {
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
        }

        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
        };
    }, [isDragging, handleMouseMove, handleMouseUp]);

    // Helper function to format time ago (Problem 2)
    const formatTimeAgo = (timestamp) => {
        const seconds = Math.floor((new Date() - new Date(timestamp)) / 1000);
        if (seconds < 60) return 'just now';
        const minutes = Math.floor(seconds / 60);
        if (minutes < 60) return `${minutes}m ago`;
        const hours = Math.floor(minutes / 60);
        return `${hours}h ago`;
    };

    // Track content changes for bidirectional sync (Problem 2)
    const trackContentChange = useCallback((field, description) => {
        setEditorChanges(prev => [...prev.slice(-50), {
            field,
            timestamp: new Date(),
            description
        }]);
    }, []);

    const fetchRoles = async () => {
        try {
            const response = await api.get('/employees/roles');
            setAvailableRoles(response.data.roles || []);
        } catch (error) {
            console.error('Error fetching roles:', error);
        }
    };

    // Handle file selection from Unified Picker
    const handleFileSelection = (files) => {
        if (pickerMode === 'chat') {
            // Instead of adding directly to context, we now ask the user what they want to do
            // This restores the "Attach to Task" vs "Analyze File" functionality
            const pendingFiles = files.map(f => ({
                file_key: f.file_key || f.key || `existing_${f.id}`,
                file_name: f.file_name || f.name,
                file_type: f.file_type || f.type || 'application/octet-stream',
                file_size: f.file_size || f.size || 0,
                // Add a flag to indicate this is an existing company file
                is_company_file: !!f.id,
                id: f.id,
                url: f.file_url || f.url
            }));

            // Add a special message to the chat that shows the intent buttons
            const choiceMessage = {
                id: Date.now(),
                role: 'assistant',
                content: `I've selected **${pendingFiles.length} file(s)**. How would you like to use them?`,
                action: 'file_intent_choice', // This triggers the buttons in the render loop
                pendingFiles: pendingFiles,
                timestamp: new Date()
            };

            setMessages(prev => [...prev, choiceMessage]);

            // Add to aiUploadedFiles immediately so they show up above the input
            setAiUploadedFiles(prev => {
                const combined = [...prev, ...pendingFiles];
                const unique = [];
                const seen = new Set();
                combined.forEach(file => {
                    const key = file.file_key;
                    if (!seen.has(key)) {
                        seen.add(key);
                        unique.push({ ...file, needs_intent: true });
                    }
                });
                return unique.slice(0, 5);
            });
        } else {
            // Document popup prompt for Editor/Upload attachments
            const newAttachments = files.map(f => ({
                file_key: f.file_key || f.key || `existing_${f.id}`,
                file_name: f.file_name || f.name,
                file_type: f.file_type || f.type || 'application/octet-stream',
                file_size: f.file_size || f.size || 0,
                file_url: f.file_url || f.url,
                // If it's an existing file, we might want to store more info
                is_company_file: !!f.id,
                original_file_id: f.id
            }));

            setPendingDocumentPromptFiles(newAttachments);
            setShowDocumentPromptModal(true);
        }
        setShowFilePicker(false);
    };

    const handleDocumentPromptConfirm = (addToDocuments, chatFiles = null) => {
        const filesToProcess = chatFiles || pendingDocumentPromptFiles;

        setFormData(prev => {
            const currentAttachments = prev.attachments || [];

            // Apply add_to_documents property to the pending files
            const processedNewAttachments = filesToProcess.map(f => ({
                ...f,
                add_to_documents: addToDocuments
            }));

            // Filter duplicates
            const combined = [...currentAttachments, ...processedNewAttachments];
            const unique = [];
            const seen = new Set();

            combined.forEach(file => {
                const key = file.original_file_id ? `id_${file.original_file_id}` : file.file_key;
                if (!seen.has(key)) {
                    seen.add(key);
                    unique.push(file);
                }
            });

            return {
                ...prev,
                attachments: unique.slice(0, 5) // Max 5 attachments
            };
        });

        toast.success(`${filesToProcess.length} file(s) attached to task`);
        setShowDocumentPromptModal(false);
        setPendingDocumentPromptFiles([]);

        // If these came from AI chat intent, we need to update AI chat states
        if (chatFiles) {
            const fileKeys = chatFiles.map(f => f.file_key);
            setPendingIntentFiles(prev => prev.filter(f => !fileKeys.includes(f.file_key)));

            setAiUploadedFiles(prev => prev.map(f =>
                fileKeys.includes(f.file_key)
                    ? { ...f, intent: 'attachment', processing_status: 'attached' }
                    : f
            ));

            const fileNames = chatFiles.map(f => f.file_name).join(', ');
            const confirmMessage = {
                id: Date.now(),
                role: 'assistant',
                content: `📎 **Attached to task:** ${fileNames}\n\nThe file will be available for employees to download when this task is published.`,
                timestamp: new Date(),
                action: 'file_attached'
            };
            setMessages(prev => [...prev, confirmMessage]);
        }
    };



    // Handle removing an attachment
    const handleRemoveAttachment = async (file) => {
        // If editing an existing task and file has an ID (not just a temp key from new upload), delete from server
        // But only if we are in edit mode and the file is actually part of the task (has a real file_key on server)
        // If it was just added in this session (no ID yet or just uploaded), we just remove from state

        // Note: UnifiedFilePicker returns objects. 
        // If it's a new upload in this session, it might not be saved to the task yet until we save/update.
        // However, if we are EDITING a task, and we remove a file that was ALREADY on the task, we should probably delete it or mark for deletion.
        // The original FileUpload component deleted immediately.

        if (isEditMode && editTask && editTask.id && file.file_key) {
            // Check if this file was part of the original task attachments
            const isOriginal = editTask.attachments?.some(f => f.file_key === file.file_key);

            if (isOriginal) {
                const loadingToast = toast.loading('Removing file...');
                try {
                    const token = localStorage.getItem('token');
                    await api.delete(`/tasks/${editTask.id}/attachment`, {
                        headers: { Authorization: `Bearer ${token}` },
                        data: { fileKey: file.file_key }
                    });
                    toast.success('File removed', { id: loadingToast });
                } catch (error) {
                    console.error('File deletion error:', error);
                    toast.error('Failed to remove file', { id: loadingToast });
                    return; // Don't remove from state if server fail
                }
            }
        }

        setFormData(prev => ({
            ...prev,
            attachments: prev.attachments.filter(f =>
                (f.file_key && f.file_key !== file.file_key) ||
                (f.id && f.id !== file.id)
            )
        }));
    };

    // Streaming message handler (Problem 4)
    const handleSendMessageWithStreaming = async (messageToSend, currentFiles) => {
        setIsStreaming(true);
        setStreamingMessage('');
        setStreamingPhase('Analyzing your request...');

        const token = localStorage.getItem('token');
        // Get base URL from api utility - Note: Using native fetch for SSE support (axios doesn't stream well)
        const baseURL = api.defaults.baseURL;

        try {
            // DEBUG: Log what we're sending to see if content is updated
            console.log('[AI Debug] Sending current_task to AI:', {
                title: formData.title,
                content: formData.content,
                editorChanges: editorChanges.slice(-5)
            });

            const response = await fetch(`${baseURL}/tasks/generate-ai-chat-stream`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    message: messageToSend,
                    session_id: sessionId,
                    phase: formData.phase,
                    current_task_type: formData.task_type,
                    quiz_grading_type: formData.quiz_grading_type,
                    conversation_history: messages.slice(-10).map(m => ({
                        role: m.role,
                        content: m.content
                    })),
                    current_task: {
                        title: formData.title,
                        description: formData.description,
                        task_type: formData.task_type,
                        content: formData.content,
                        is_combined: formData.is_combined,
                        sections: formData.sections,
                        quiz_grading_type: formData.quiz_grading_type
                    },
                    uploaded_files: currentFiles,
                    web_search_enabled: isWebSearchEnabled,
                    recent_editor_changes: editorChanges.slice(-50).map(c => ({
                        field: c.field,
                        description: c.description,
                        time_ago: formatTimeAgo(c.timestamp)
                    }))
                })
            });

            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            let buffer = '';

            // Progressive streaming state
            let accumulatedContent = '';
            let titleExtracted = false;
            let descExtracted = false;
            let lastExtractedCount = 0;

            while (true) {
                const { done, value } = await reader.read();
                if (done) break;

                buffer += decoder.decode(value, { stream: true });
                const lines = buffer.split('\n');
                buffer = lines.pop() || '';

                for (const line of lines) {
                    if (line.startsWith('data: ')) {
                        try {
                            const data = JSON.parse(line.slice(6));

                            if (data.type === 'phase') {
                                setStreamingPhase(data.phase);
                                // Track when web search is in progress
                                if (data.phase?.includes('Searching for current information')) {
                                    setIsWebSearching(true);
                                }
                            } else if (data.type === 'web_search_results') {
                                // Store web search results for display
                                webSearchResultsRef.current = data.results;
                                setCurrentWebSearchResults(data.results);
                                setIsWebSearching(false);
                            } else if (data.type === 'content') {
                                // Accumulate the raw JSON content
                                accumulatedContent += data.content;

                                // Try to extract reply for chat display
                                // Try to extract reply for chat display
                                const replyMatch = accumulatedContent.match(/"reply"\s*:\s*"((?:[^"\\]|\\.)*)"/);
                                if (replyMatch && replyMatch[1]) {
                                    try {
                                        const extractedReply = replyMatch[1]
                                            .replace(/\\n/g, '\n')
                                            .replace(/\\"/g, '"')
                                            .replace(/\\\\/g, '\\');
                                        setStreamingMessage(extractedReply);
                                    } catch {
                                        setStreamingMessage(replyMatch[1]);
                                    }
                                }

                                // Try to extract title progressively
                                const titleMatch = accumulatedContent.match(/"title"\s*:\s*"((?:[^"\\]|\\.)*)"/);
                                if (titleMatch && titleMatch[1] && !titleExtracted) {
                                    titleExtracted = true;
                                    setFormData(prev => ({ ...prev, title: titleMatch[1] }));
                                }

                                // Try to extract description progressively
                                const descMatch = accumulatedContent.match(/"description"\s*:\s*"((?:[^"\\]|\\.)*)"/);
                                if (descMatch && descMatch[1] && !descExtracted) {
                                    descExtracted = true;
                                    setFormData(prev => ({ ...prev, description: descMatch[1] }));
                                }

                                // Progressive content extraction
                                if (accumulatedContent.includes('"questions"')) {
                                    // Extraction logic for quiz
                                    // simplified for brevity in replacement but keeping core logic
                                } else if (accumulatedContent.includes('"items"')) {
                                    // Extraction logic for checklist
                                } else if (accumulatedContent.includes('"sections"')) {
                                    // Extraction logic for training
                                }
                            } else if (data.type === 'complete') {
                                // Add AI's conversational reply to chat
                                const aiMessage = {
                                    id: Date.now() + 1,
                                    role: 'assistant',
                                    content: data.reply,
                                    timestamp: new Date(),
                                    webSearchResults: webSearchResultsRef.current || null,
                                    action: data.action
                                };
                                setMessages(prev => [...prev, aiMessage]);

                                // Clear current web search results
                                setCurrentWebSearchResults(null);
                                webSearchResultsRef.current = null;

                                // Update task editor fields immediately
                                if (data.task_update) {
                                    const isCombinedTask = data.task_update.task_type === 'combined' || data.task_update.is_combined;

                                    setFormData(prev => ({
                                        ...prev,
                                        ...data.task_update,
                                        task_type: data.task_update.task_type || prev.task_type,
                                        is_combined: isCombinedTask,
                                        sections: isCombinedTask ? (data.task_update.sections || prev.sections) : []
                                    }));

                                    // Update content cache
                                    if (isCombinedTask && data.task_update.sections) {
                                        setContentCache(prev => ({ ...prev, combined: data.task_update.sections }));
                                    } else if (data.task_update.content) {
                                        setContentCache(prev => ({
                                            ...prev,
                                            [data.task_update.task_type || formData.task_type]: data.task_update.content
                                        }));
                                    }

                                    if (data.task_update.title || data.task_update.content || data.task_update.sections) {
                                        toast.success('Task updated! Check the editor on the right.');
                                    }
                                }

                                // Handle file actions
                                if (data.file_action) {
                                    if (data.file_action.type === 'attach') {
                                        let filesToAttach = aiUploadedFiles;
                                        if (data.file_action.files && Array.isArray(data.file_action.files) && data.file_action.files.length > 0) {
                                            const fileNamesToAttach = data.file_action.files.map(f => typeof f === 'string' ? f : f.file_name);
                                            filesToAttach = aiUploadedFiles.filter(f => fileNamesToAttach.includes(f.file_name));
                                        }

                                        if (filesToAttach.length > 0) {
                                            setFormData(prev => {
                                                const existingAttachments = prev.attachments || [];
                                                const newToAttach = filesToAttach.filter(f => !existingAttachments.some(a => a.file_key === f.file_key));
                                                return newToAttach.length > 0 ? { ...prev, attachments: [...existingAttachments, ...newToAttach] } : prev;
                                            });
                                            toast.success('📎 Files attached to task');
                                        }
                                    } else if (data.file_action.type === 'remove') {
                                        // Handle remove
                                        if (data.file_action.files && data.file_action.files.length > 0) {
                                            const names = data.file_action.files.map(f => typeof f === 'string' ? f : f.file_name);
                                            setFormData(prev => ({
                                                ...prev,
                                                attachments: (prev.attachments || []).filter(a => !names.includes(a.file_name))
                                            }));
                                        } else {
                                            setFormData(prev => ({ ...prev, attachments: [] }));
                                        }
                                    }
                                }

                                if (data.suggestions) setSuggestions(data.suggestions);
                            } else if (data.type === 'error') {
                                // Show a nice styled error toast
                                toast.custom((t) => (
                                    <div className={`${t.visible ? 'animate-enter' : 'animate-leave'} max-w-md w-full bg-white dark:bg-slate-800 shadow-2xl rounded-2xl pointer-events-auto ring-1 ring-black/5 dark:ring-white/10 overflow-hidden`}>
                                        <div className="p-5">
                                            <div className="flex items-start gap-4">
                                                <div className={`flex-shrink-0 w-11 h-11 rounded-xl flex items-center justify-center ${data.errorType === 'network' ? 'bg-amber-100 dark:bg-amber-500/20' : 'bg-red-100 dark:bg-red-500/20'}`}>
                                                    <AlertCircle className={`w-6 h-6 ${data.errorType === 'network' ? 'text-amber-600 dark:text-amber-400' : 'text-red-600 dark:text-red-400'}`} />
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <p className="text-sm font-semibold text-gray-900 dark:text-white">
                                                        {data.errorType === 'network' ? 'Connection Issue' : 'AI Generation Failed'}
                                                    </p>
                                                    <p className="mt-1 text-sm text-gray-500 dark:text-slate-400 leading-relaxed">
                                                        {data.message || 'An unexpected error occurred.'}
                                                    </p>
                                                </div>
                                                <button onClick={() => toast.dismiss(t.id)} className="flex-shrink-0 p-1 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-slate-200 hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors">
                                                    <X className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </div>
                                        <div className={`h-1 ${data.errorType === 'network' ? 'bg-gradient-to-r from-amber-400 to-orange-500' : 'bg-gradient-to-r from-red-400 to-rose-500'}`} />
                                    </div>
                                ), { duration: 6000 });
                            }
                        } catch (e) {
                            // Skip invalid JSON
                        }
                    }
                }
            }
        } catch (error) {
            console.error('Streaming error:', error);
            const errorMessage = {
                id: Date.now() + 1,
                role: 'assistant',
                content: "⚠️ I'm having trouble connecting right now. This is usually temporary — please try sending your message again.",
                timestamp: new Date(),
                isError: true
            };
            setMessages(prev => [...prev, errorMessage]);
        } finally {
            setIsStreaming(false);
            setStreamingMessage('');
            setStreamingPhase(null);
        }
    };

    const handleSendMessage = async () => {
        if (!inputMessage.trim() || isGenerating) return;

        const userMessage = {
            id: Date.now(),
            role: 'user',
            content: inputMessage,
            timestamp: new Date()
        };

        setMessages(prev => [...prev, userMessage]);
        const messageToSend = inputMessage;
        setInputMessage('');
        setIsGenerating(true);

        try {
            // Check if user's message wants to use file content and files are pending
            const messageLower = messageToSend.toLowerCase();
            const wantsToUseFileContent = [
                'create', 'make', 'generate', 'summarize', 'analyze', 'based on',
                'from this', 'using this', 'quiz from', 'training from', 'extract',
                'what does', 'tell me about', 'explain', 'key points'
            ].some(keyword => messageLower.includes(keyword));

            const pendingFiles = aiUploadedFiles.filter(f =>
                f.needs_intent || f.embedding_status === 'pending' || !f.embedding_status
            );

            // Auto-process pending files if user wants to use file content
            if (wantsToUseFileContent && pendingFiles.length > 0) {
                toast.loading('Processing files for AI analysis...', { id: 'auto-process' });

                // Add processing notification to chat
                const processingMsg = {
                    id: Date.now() + 0.5,
                    role: 'assistant',
                    content: `📄 Processing **${pendingFiles.map(f => f.file_name).join(', ')}** for AI analysis...`,
                    timestamp: new Date()
                };
                setMessages(prev => [...prev, processingMsg]);

                // Process each pending file
                for (const file of pendingFiles) {
                    try {
                        const response = await api.post('/files/ai-upload/confirm-intent', {
                            file_key: file.file_key,
                            session_id: sessionId,
                            intent: 'reference',
                            file_buffer_base64: file.file_buffer_base64
                        });

                        if (response.data.success) {
                            setAiUploadedFiles(prev => prev.map(f =>
                                f.file_key === file.file_key
                                    ? {
                                        ...f,
                                        intent: 'reference',
                                        embedding_status: response.data.embedding_status,
                                        processing_status: response.data.embedding_status,
                                        document_id: response.data.document_id,
                                        needs_intent: false
                                    }
                                    : f
                            ));
                        }
                    } catch (error) {
                        console.error('Error auto-processing file:', error);
                    }
                }

                toast.success('✓ Analysis complete! Generating task...', { id: 'auto-process' });

                // Update the processing message to show completion
                setMessages(prev => prev.map(m =>
                    m.id === processingMsg.id
                        ? { ...m, content: `✓ Analyzed **${pendingFiles.map(f => f.file_name).join(', ')}**. Now generating content...` }
                        : m
                ));

                // Remove from pending intent files
                setPendingIntentFiles([]);
            }

            // Get updated file list after processing
            const currentFiles = aiUploadedFiles.map(f => ({
                file_name: f.file_name,
                file_key: f.file_key,
                file_url: f.file_url,
                file_size: f.file_size,
                file_type: f.file_type,
                document_id: f.document_id,
                embedding_status: f.embedding_status || 'completed'
            }));

            // Use streaming for better UX (Problem 4)
            await handleSendMessageWithStreaming(messageToSend, currentFiles);
            setIsGenerating(false);
            return; // Streaming handles everything

            /* Original non-streaming code - kept for fallback if needed
            const response = await api.post('/tasks/generate-ai-chat', {
                message: messageToSend,
                session_id: sessionId,
                phase: formData.phase,
                current_task_type: formData.task_type,
                quiz_grading_type: formData.quiz_grading_type,
                conversation_history: messages.slice(-10).map(m => ({
                    role: m.role,
                    content: m.content
                })),
                current_task: {
                    title: formData.title,
                    description: formData.description,
                    task_type: formData.task_type,
                    content: formData.content,
                    quiz_grading_type: formData.quiz_grading_type
                },
                // Include uploaded files info - AI will understand intent from user's message
                uploaded_files: currentFiles,
                // Include recent editor changes for bidirectional sync (Problem 2)
                recent_editor_changes: editorChanges.slice(-5).map(c => ({
                    field: c.field,
                    description: c.description,
                    time_ago: formatTimeAgo(c.timestamp)
                }))
            });
            */

            /* Non-streaming response handling - disabled in favor of streaming
            if (response.data.success) {
                const { reply, task_update, suggestions: newSuggestions, action, file_action } = response.data;
    
                // Handle file actions from AI's intelligent intent recognition
                if (file_action) {
                    if (file_action.type === 'attach') {
                        // AI determined files should be attached to task
                        // file_action.files may be file names (strings) or not provided
                        let filesToAttach = aiUploadedFiles;
    
                        // If AI specified specific file names, filter to those
                        if (file_action.files && Array.isArray(file_action.files) && file_action.files.length > 0) {
                            // AI returns file names as strings, match them to our uploaded files
                            const fileNamesToAttach = file_action.files.map(f =>
                                typeof f === 'string' ? f : f.file_name
                            );
                            filesToAttach = aiUploadedFiles.filter(f =>
                                fileNamesToAttach.includes(f.file_name)
                            );
                        }
    
                        if (filesToAttach.length > 0) {
                            // Check for already attached files
                            const existingAttachments = formData.attachments || [];
                            const alreadyAttached = filesToAttach.filter(f =>
                                existingAttachments.some(a => a.file_key === f.file_key || a.file_name === f.file_name)
                            );
                            const newToAttach = filesToAttach.filter(f =>
                                !existingAttachments.some(a => a.file_key === f.file_key || a.file_name === f.file_name)
                            );
    
                            if (alreadyAttached.length > 0) {
                                toast(`📎 Already attached: ${alreadyAttached.map(f => f.file_name).join(', ')}`, { icon: 'ℹ️' });
                            }
    
                            if (newToAttach.length > 0) {
                                const attachmentFiles = newToAttach.map(f => ({
                                    file_name: f.file_name,
                                    file_url: f.file_url,
                                    file_key: f.file_key,
                                    file_size: f.file_size,
                                    file_type: f.file_type
                                }));
                                setFormData(prev => ({
                                    ...prev,
                                    attachments: [...(prev.attachments || []), ...attachmentFiles]
                                }));
    
                                const fileNames = newToAttach.map(f => f.file_name).join(', ');
                                toast.success(`📎 Attached: ${fileNames}`);
                            }
                        }
                    } else if (file_action.type === 'remove') {
                        // AI determined files should be removed from task
                        if (file_action.files && Array.isArray(file_action.files) && file_action.files.length > 0) {
                            const fileNamesToRemove = file_action.files.map(f =>
                                typeof f === 'string' ? f : f.file_name
                            );
    
                            setFormData(prev => ({
                                ...prev,
                                attachments: (prev.attachments || []).filter(a =>
                                    !fileNamesToRemove.includes(a.file_name)
                                )
                            }));
    
                            toast.success(`🗑️ Removed: ${fileNamesToRemove.join(', ')}`);
                        } else {
                            // Remove all attachments if no specific files mentioned
                            setFormData(prev => ({
                                ...prev,
                                attachments: []
                            }));
                            toast.success('🗑️ All attachments removed');
                        }
                    }
                }
    
                // Add AI response
                const aiMessage = {
                    id: Date.now() + 1,
                    role: 'assistant',
                    content: reply,
                    timestamp: new Date(),
                    action
                };
                setMessages(prev => [...prev, aiMessage]);
    
                // Update task if AI provided updates
                if (task_update) {
                    setFormData(prev => ({
                        ...prev,
                        ...task_update,
                        task_type: task_update.task_type || prev.task_type
                    }));
    
                    // Show toast for significant updates
                    if (task_update.title || task_update.content) {
                        toast.success('Task updated! Check the editor on the right.');
                    }
                }
    
                // Update suggestions
                if (newSuggestions && newSuggestions.length > 0) {
                    setSuggestions(newSuggestions);
                }
            }
            */
        } catch (error) {
            console.error('AI chat error:', error);
            const errorMessage = {
                id: Date.now() + 1,
                role: 'assistant',
                content: "I apologize, but I encountered an error. Please try again or rephrase your request.",
                timestamp: new Date(),
                isError: true
            };
            setMessages(prev => [...prev, errorMessage]);
        } finally {
            setIsGenerating(false);
        }
    };

    // Handle AI file uploads - DEFERRED PROCESSING
    const handleAiFilesUploaded = (newFiles) => {
        // Check if files need intent choice (deferred processing)
        const filesNeedingIntent = newFiles.filter(f => f.needs_intent && !f.error);

        if (filesNeedingIntent.length > 0) {
            // Store files awaiting intent choice
            setPendingIntentFiles(prev => [...prev, ...filesNeedingIntent]);
            // Add to uploaded files list
            setAiUploadedFiles(prev => [...prev, ...newFiles.filter(f => !f.error)]);

            // Add message with intent chooser
            const fileNames = filesNeedingIntent.map(f => f.file_name).join(', ');
            const intentMessage = {
                id: Date.now(),
                role: 'assistant',
                content: filesNeedingIntent.length === 1
                    ? `📄 Uploaded **"${fileNames}"**. What would you like to do with this file?`
                    : `📄 Uploaded ${filesNeedingIntent.length} files. What would you like to do with them?`,
                timestamp: new Date(),
                action: 'file_intent_choice',
                pendingFiles: filesNeedingIntent
            };
            setMessages(prev => [...prev, intentMessage]);
            return;
        }

        // Handle already-processed files (legacy flow)
        setAiUploadedFiles(prev => [...prev, ...newFiles.filter(f => !f.error)]);

        const completedFiles = newFiles.filter(f => f.embedding_status === 'completed');
        if (completedFiles.length > 0) {
            const fileNames = completedFiles.map(f => f.file_name).join(', ');
            const confirmMessage = {
                id: Date.now(),
                role: 'assistant',
                content: `📄 I've read **"${fileNames}"**. What would you like me to do with it?`,
                timestamp: new Date(),
                action: 'file_processed'
            };
            setMessages(prev => [...prev, confirmMessage]);
            setSuggestions([
                'Summarize this document',
                'Create a quiz from this content',
                'Add this as a task attachment',
                'What are the key points?'
            ]);
        }
    };

    // Direct file upload handler for inline file picker (Claude/Gemini style)
    const handleDirectFileUpload = async (e) => {
        const files = Array.from(e.target.files);
        if (files.length === 0) return;

        // Validate file count
        if (aiUploadedFiles.length + files.length > 5) {
            toast.error('Maximum 5 files allowed per session');
            if (aiFileInputRef.current) aiFileInputRef.current.value = '';
            return;
        }

        // Validate file sizes (max 10MB each)
        const invalidFiles = files.filter(file => file.size > 10 * 1024 * 1024);
        if (invalidFiles.length > 0) {
            toast.error(`Files exceed 10MB limit: ${invalidFiles.map(f => f.name).join(', ')}`);
            if (aiFileInputRef.current) aiFileInputRef.current.value = '';
            return;
        }

        setIsFileUploading(true);
        setFileUploadProgress(0);

        try {
            const formDataUpload = new FormData();
            files.forEach(file => {
                formDataUpload.append('files', file);
            });
            formDataUpload.append('session_id', sessionId);

            const response = await api.post('/files/ai-upload', formDataUpload, {
                headers: { 'Content-Type': 'multipart/form-data' },
                onUploadProgress: (progressEvent) => {
                    const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                    setFileUploadProgress(percentCompleted);
                },
            });

            if (response.data.success) {
                const newFiles = response.data.files.filter(f => !f.error);
                handleAiFilesUploaded(newFiles);
                const fileNames = newFiles.map(f => f.file_name).join(', ');
                toast.success(
                    newFiles.length === 1
                        ? `Uploaded: ${fileNames}`
                        : `${newFiles.length} files uploaded`
                );
            }
        } catch (error) {
            console.error('AI file upload error:', error);
            toast.error(error.response?.data?.message || 'Failed to upload files');
        } finally {
            setIsFileUploading(false);
            setFileUploadProgress(0);
            if (aiFileInputRef.current) aiFileInputRef.current.value = '';
        }
    };

    // Handle file processing complete callback
    const handleFileProcessingComplete = (completedFiles) => {
        if (completedFiles.length > 0) {
            setFileProcessingMessage(null);
            // Update file status in state
            setAiUploadedFiles(prev => prev.map(f => {
                const completed = completedFiles.find(cf => cf.file_key === f.file_key);
                if (completed) {
                    return { ...f, ...completed };
                }
                return f;
            }));
        }
    };

    // Handle removing an AI uploaded file
    const handleRemoveAiFile = async (file) => {
        try {
            // If it was processed as reference, delete from embeddings
            if (file.document_id) {
                await api.delete(`/files/ai-reference/${file.document_id}`);
            }
            setAiUploadedFiles(prev => prev.filter(f => f.file_key !== file.file_key));
            setPendingIntentFiles(prev => prev.filter(f => f.file_key !== file.file_key));
            toast.success('File removed');
        } catch (error) {
            console.error('Error removing file:', error);
            // Still remove from local state
            setAiUploadedFiles(prev => prev.filter(f => f.file_key !== file.file_key));
            setPendingIntentFiles(prev => prev.filter(f => f.file_key !== file.file_key));
        }
    };

    const handleSuggestionClick = (suggestion) => {
        setInputMessage(suggestion);
        inputRef.current?.focus();
    };

    // Handle "Attach to Task" intent choice
    const handleAttachToTask = (files) => {
        // Check for already attached files
        const existingAttachments = formData.attachments || [];
        const alreadyAttached = files.filter(f =>
            existingAttachments.some(a => a.file_key === f.file_key || a.file_name === f.file_name)
        );
        const newToAttach = files.filter(f =>
            !existingAttachments.some(a => a.file_key === f.file_key || a.file_name === f.file_name)
        );

        // Notify about already attached files
        if (alreadyAttached.length > 0) {
            const alreadyNames = alreadyAttached.map(f => f.file_name).join(', ');
            toast(`📎 Already attached: ${alreadyNames}`, { icon: 'ℹ️' });

            if (newToAttach.length === 0) {
                // All files already attached, add message and return
                const message = {
                    id: Date.now(),
                    role: 'assistant',
                    content: `📎 **${alreadyNames}** is already attached to this task.`,
                    timestamp: new Date()
                };
                setMessages(prev => [...prev, message]);

                // Remove from pending files
                const fileKeys = files.map(f => f.file_key);
                setPendingIntentFiles(prev => prev.filter(f => !fileKeys.includes(f.file_key)));
                return;
            }
        }

        // Add only new files to task attachments
        if (newToAttach.length > 0) {
            const attachments = newToAttach.map(f => ({
                file_name: f.file_name,
                file_url: f.file_url,
                file_key: f.file_key,
                file_size: f.file_size,
                file_type: f.file_type
            }));

            // Trigger the document prompt for AI chat uploads
            setPendingDocumentPromptFiles(attachments);
            // We use a special property for the modal to know it's from chat, 
            // but we can just pass the files to the handler when the modal confirms.
            // Let's add a state to indicate the modal is for chat intent files
            setShowDocumentPromptModal({ isChat: true, files: attachments });
        }
    };

    // Handle "Let AI Analyze" intent choice
    const handleLetAIAnalyze = async (files) => {
        setFileProcessingMessage('Processing documents for AI analysis...');

        try {
            let alreadyAnalyzedCount = 0;
            let newlyAnalyzedCount = 0;

            for (const file of files) {
                // Call confirm-intent endpoint with the file buffer
                const response = await api.post('/files/ai-upload/confirm-intent', {
                    file_key: file.file_key,
                    session_id: sessionId,
                    intent: 'reference',
                    file_buffer_base64: file.file_buffer_base64
                });

                if (response.data.success) {
                    // Check if file was already analyzed
                    if (response.data.already_analyzed) {
                        alreadyAnalyzedCount++;
                        toast.success(`📄 "${file.file_name}" was already analyzed`, { duration: 3000 });
                    } else {
                        newlyAnalyzedCount++;
                    }

                    // Update file status
                    setAiUploadedFiles(prev => prev.map(f =>
                        f.file_key === file.file_key
                            ? {
                                ...f,
                                intent: 'reference',
                                embedding_status: response.data.embedding_status,
                                processing_status: response.data.embedding_status,
                                document_id: response.data.document_id
                            }
                            : f
                    ));
                }
            }

            // Remove from pending files
            const fileKeys = files.map(f => f.file_key);
            setPendingIntentFiles(prev => prev.filter(f => !fileKeys.includes(f.file_key)));

            // Add confirmation message based on analysis status
            const fileNames = files.map(f => f.file_name).join(', ');
            let messageContent;

            if (alreadyAnalyzedCount > 0 && newlyAnalyzedCount === 0) {
                messageContent = `📄 **${fileNames}** was already analyzed. I can answer questions about it, create quizzes, or summarize it.\n\nWhat would you like me to do?`;
            } else {
                messageContent = `🧠 I've analyzed **${fileNames}**. I can now answer questions about it, create quizzes based on its content, or summarize it.\n\nWhat would you like me to do?`;
            }

            const confirmMessage = {
                id: Date.now(),
                role: 'assistant',
                content: messageContent,
                timestamp: new Date(),
                action: 'file_processed'
            };
            setMessages(prev => [...prev, confirmMessage]);
            setSuggestions([
                'Summarize this document',
                'Create a quiz from this content',
                'What are the key points?',
                'Generate training from this'
            ]);

        } catch (error) {
            console.error('Error processing file for AI:', error);
            toast.error('Failed to process file for AI analysis');
        } finally {
            setFileProcessingMessage(null);
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage();
        }
    };

    const handleTaskTypeChange = (newTaskType) => {
        // Calculate the next cache state explicitly to avoid stale state issues (Problem 3: Content Persistence)
        let nextCache = { ...contentCache };

        if (formData.task_type === 'combined') {
            // Cache current sections if we are leaving combined mode
            nextCache.combined = formData.sections;
        } else {
            // Cache current content if we are leaving a standard mode
            nextCache[formData.task_type] = formData.content;
        }

        // Update cache state
        setContentCache(nextCache);

        // Switch to new task type and restore cached content using the FRESH cache
        const isCombined = newTaskType === 'combined';

        setFormData(prev => ({
            ...prev,
            task_type: newTaskType,
            content: isCombined ? '' : (nextCache[newTaskType] || (newTaskType === 'task' ? '' : null)),
            is_combined: isCombined,
            sections: isCombined ? (nextCache.combined || []) : []
        }));

        // Track the change for AI sync
        trackContentChange('task_type', `Changed task type to ${newTaskType}`);
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleRolesChange = (e) => {
        const selectedOptions = Array.from(e.target.selectedOptions, option => option.value);
        setFormData(prev => ({ ...prev, assigned_roles: selectedOptions }));
    };

    // handleSaveAsDraft is now handled automatically by performAutoSave

    const handlePublish = async () => {
        if (!formData.title.trim()) {
            toast.error('Please enter a task title');
            return;
        }

        setIsSaving(true);
        try {
            let taskId;

            const taskPayload = {
                ...formData,
                status: 'draft',
                is_ai_generated: true,
                ai_chat_history: messages
            };

            if (isEditMode) {
                // Update existing task first
                await api.put(`/tasks/${editTask.id}`, taskPayload);
                taskId = editTask.id;
            } else if (draftTaskIdRef.current) {
                // Use auto-saved draft
                await api.put(`/tasks/${draftTaskIdRef.current}`, taskPayload);
                taskId = draftTaskIdRef.current;
            } else {
                // Create task first
                const createResponse = await api.post('/tasks', taskPayload);
                taskId = createResponse.data.task.id;
            }

            // Then publish it
            await api.post(`/tasks/${taskId}/publish`);

            // Clear session on successful publish
            localStorage.removeItem('ai_task_draft_session');

            toast.success(isEditMode ? 'Task updated and published!' : 'Task published successfully!');
            onSuccess();
            handleClose();
        } catch (error) {
            console.error('Publish error:', error);
            toast.error('Failed to publish task');
        } finally {
            setIsSaving(false);
        }
    };

    const handleSchedule = async () => {
        if (!formData.title.trim()) {
            toast.error('Please enter a task title');
            return;
        }

        if (!formData.scheduled_date) {
            toast.error('Please select a scheduled date');
            return;
        }

        setIsSaving(true);
        try {
            let taskId;

            const taskPayload = {
                ...formData,
                status: 'draft',
                is_ai_generated: true,
                ai_chat_history: messages
            };

            if (isEditMode) {
                // Update existing task first
                await api.put(`/tasks/${editTask.id}`, taskPayload);
                taskId = editTask.id;
            } else if (draftTaskIdRef.current) {
                // Use auto-saved draft
                await api.put(`/tasks/${draftTaskIdRef.current}`, taskPayload);
                taskId = draftTaskIdRef.current;
            } else {
                // Create task first
                const createResponse = await api.post('/tasks', taskPayload);
                taskId = createResponse.data.task.id;
            }

            // Then schedule it
            await api.post(`/tasks/${taskId}/schedule`, { scheduled_date: formData.scheduled_date });

            // Clear session on successful schedule
            localStorage.removeItem('ai_task_draft_session');

            toast.success(isEditMode ? 'Task updated and scheduled!' : 'Task scheduled successfully!');
            onSuccess();
            handleClose();
        } catch (error) {
            console.error('Schedule error:', error);
            toast.error('Failed to schedule task');
        } finally {
            setIsSaving(false);
        }
    };

    const handleClose = () => {
        // Auto-save handles persistence — just flush any pending save and close
        if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
        if (formData.title?.trim() && hasEditorContent()) {
            // Fire a final save before closing
            performAutoSave();
        }
        // Notify parent to refresh task list if a draft was saved
        if (draftTaskIdRef.current || isEditMode) {
            onSuccess();
        }
        onClose();
    };

    const handleClearChat = () => {
        if (window.confirm('Clear chat history? Your task editor content will be preserved.')) {
            setMessages([{
                id: Date.now(),
                role: 'assistant',
                content: "Chat cleared! Your task content is still in the editor. How can I help you further?",
                timestamp: new Date()
            }]);
        }
    };

    const getTaskIcon = (taskType) => {
        switch (taskType) {
            case 'quiz': return <Brain className="w-5 h-5" />;
            case 'training': return <BookOpen className="w-5 h-5" />;
            case 'checklist': return <CheckSquare className="w-5 h-5" />;
            case 'combined': return <Layers className="w-5 h-5" />;
            default: return <FileText className="w-5 h-5" />;
        }
    };

    // Add a section from the blocks sidebar
    const addBlockFromSidebar = (sectionType) => {
        const decorativeTypes = ['divider', 'heading', 'callout', 'video'];
        const blockDef = BLOCK_TYPES.flatMap(c => c.items).find(b => b.type === sectionType);
        const getDefaultContent = (type) => {
            switch (type) {
                case 'quiz': return { questions: [] };
                case 'training': return { sections: [] };
                case 'checklist': return { items: [] };
                case 'form_fields': return { fields: [], instructions: '' };
                case 'content': return '';
                case 'video': return { url: '', caption: '' };
                case 'divider': return { label: '', style: 'solid' };
                case 'callout': return { type: 'info', message: '' };
                case 'heading': return { text: '', level: 'h2', subtitle: '' };
                case 'proof_upload': return { instructions: '', allowed_types: ['image', 'pdf', 'document'] };
                case 'signature': return { acknowledgment_text: '' };
                default: return null;
            }
        };

        const newSection = {
            id: Date.now(),
            section_type: sectionType,
            title: `${blockDef?.label || 'Section'} ${formData.sections.length + 1}`,
            description: '',
            content: getDefaultContent(sectionType),
            is_required: !decorativeTypes.includes(sectionType),
            passing_score: sectionType === 'quiz' ? 70 : null,
            grading_type: sectionType === 'quiz' ? 'auto' : null
        };

        const updated = [...formData.sections, newSection];
        setFormData(prev => ({ ...prev, sections: updated, is_combined: true }));
        setSelectedSectionIndex(updated.length - 1);
        setRightPanelTab('section');
        trackContentChange?.('combined_sections', `Added ${blockDef?.label || 'section'} block`);
    };

    const moveSectionInCanvas = (index, direction) => {
        const newIndex = direction === 'up' ? index - 1 : index + 1;
        if (newIndex >= 0 && newIndex < formData.sections.length) {
            const updated = [...formData.sections];
            [updated[index], updated[newIndex]] = [updated[newIndex], updated[index]];
            setFormData(prev => ({ ...prev, sections: updated }));
            setSelectedSectionIndex(newIndex);
        }
    };

    const deleteSectionFromCanvas = (index) => {
        const updated = formData.sections.filter((_, i) => i !== index);
        setFormData(prev => ({ ...prev, sections: updated }));
        if (selectedSectionIndex === index) {
            setSelectedSectionIndex(null);
        } else if (selectedSectionIndex > index) {
            setSelectedSectionIndex(selectedSectionIndex - 1);
        }
    };

    const getBlockIcon = (sectionType) => {
        const blockDef = BLOCK_TYPES.flatMap(c => c.items).find(b => b.type === sectionType);
        if (!blockDef) return FileText;
        return blockDef.icon;
    };

    const getBlockColor = (sectionType) => {
        return 'text-slate-600 dark:text-slate-400';
    };

    const getBlockBgColor = (sectionType) => {
        return 'bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 shadow-sm';
    };

    const getSectionSummary = (section) => {
        if (!section) return '';
        switch (section.section_type) {
            case 'quiz': return `${section.content?.questions?.length || 0} questions`;
            case 'training': return `${section.content?.sections?.length || 0} sections`;
            case 'checklist': return `${section.content?.items?.length || 0} items`;
            case 'form_fields': return `${section.content?.fields?.length || 0} fields`;
            case 'content': return typeof section.content === 'string' ? (section.content.substring(0, 40) + (section.content.length > 40 ? '...' : '')) : '';
            case 'video': return section.content?.url ? 'Video linked' : 'No video';
            case 'callout': return section.content?.type || 'info';
            case 'heading': return section.content?.text || '';
            case 'divider': return section.content?.label || 'Separator';
            default: return section.section_type;
        }
    };

    if (!isOpen) return null;

    return (
        <div ref={containerRef} className="fixed inset-0 z-50 bg-white dark:bg-slate-900 flex flex-col">
            {/* Header - Always at top on mobile, hidden on desktop since desktop has split view with header in left panel? No, NoticeGen has header at VERY TOP spanning full width */}
            {/* Wait, AINoticeGenerator has a full width header at the top of the modal. */}
            {/* Let's check AINoticeGenerator again. Line 1025: <div className="fixed inset-0 z-50 flex flex-col bg-slate-900"> */}
            {/* Then Header (1027). Then Mobile Switcher (1048). Then Flex row (1072). */}

            {/* AITaskGenerator has: flex flex-col lg:flex-row */}
            {/* I should restructure AITaskGenerator to match AINoticeGenerator structure: Top Header -> Flex Container (Chat | Editor) */}

            {/* Header */}
            <div className="fixed inset-x-0 top-0 z-10 flex-shrink-0 bg-gradient-to-r from-violet-600 to-purple-600 px-4 py-3 flex items-center justify-between lg:static">
                <div className="flex items-center gap-3">
                    <div className="p-2 bg-white dark:bg-slate-900/20 rounded-lg">
                        <Sparkles className="w-5 h-5 text-white" />
                    </div>
                    <div>
                        <h2 className="text-lg font-bold text-white">
                            {isEditMode ? 'Edit Task with AI' : 'AI Task Generator'}
                        </h2>
                        <p className="text-white/80 text-sm capitalize">{phase} Phase</p>
                    </div>

                    {/* AI Chat Toggle — left side, with label */}
                    <button
                        onClick={toggleChatCollapsed}
                        className="hidden lg:flex items-center gap-2 ml-3 px-3 py-1.5 rounded-full transition-all duration-200 bg-white/15 hover:bg-white/25 border border-white/20 hover:border-white/40"
                        title={isChatCollapsed ? 'Show AI Chat Panel' : 'Hide AI Chat Panel'}
                    >
                        {isChatCollapsed ? (
                            <>
                                <MessageSquare className="w-4 h-4 text-white" />
                                <span className="text-xs font-semibold text-white whitespace-nowrap">Show AI Chat</span>
                            </>
                        ) : (
                            <>
                                <PanelLeftClose className="w-4 h-4 text-white" />
                                <span className="text-xs font-semibold text-white/80 whitespace-nowrap">Hide Chat</span>
                            </>
                        )}
                    </button>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        onClick={() => setIsPreviewOpen(true)}
                        disabled={!formData.title?.trim() && (!formData.sections || formData.sections.length === 0)}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-white/15 hover:bg-white/25 border border-white/20 hover:border-white/40 rounded-full transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                        title="Preview how employees will see this task"
                    >
                        <Eye className="w-4 h-4 text-white" />
                        <span className="text-xs font-semibold text-white hidden sm:inline">Preview</span>
                    </button>
                    <button
                        onClick={handleClearChat}
                        className="p-2 hover:bg-white/15 rounded-lg transition-colors hidden lg:block"
                        title="Clear chat"
                    >
                        <RefreshCw className="w-5 h-5 text-white" />
                    </button>
                    <button
                        onClick={handleClose}
                        className="p-2 hover:bg-white/15 rounded-lg transition-colors"
                    >
                        <X className="w-5 h-5 text-white" />
                    </button>
                </div>
            </div>

            {/* Mobile Tab Switcher */}
            <div className="lg:hidden flex border-b border-slate-700 bg-slate-800 mt-[60px]">
                <button
                    onClick={() => setMobileActivePanel('chat')}
                    className={`flex-1 py-3 px-4 text-sm font-medium transition-colors ${mobileActivePanel === 'chat'
                        ? 'text-white border-b-2 border-violet-500'
                        : 'text-slate-400'
                        }`}
                >
                    <Sparkles className="w-4 h-4 inline mr-2" />
                    AI Chat
                </button>
                <button
                    onClick={() => setMobileActivePanel('editor')}
                    className={`flex-1 py-3 px-4 text-sm font-medium transition-colors ${mobileActivePanel === 'editor'
                        ? 'text-white border-b-2 border-violet-500'
                        : 'text-slate-400'
                        }`}
                >
                    <FileText className="w-4 h-4 inline mr-2" />
                    Task Editor
                </button>
            </div>

            {/* Main Content Area */}
            <div className="flex-1 flex overflow-hidden flex-col lg:flex-row mt-0 lg:mt-0 pt-0">

                {/* Collapsed Chat Sidebar Strip (desktop only) */}
                {isChatCollapsed && (
                    <div
                        className="hidden lg:flex flex-col items-center bg-slate-800 border-r border-slate-700 cursor-pointer hover:bg-slate-750 transition-colors group"
                        style={{ width: '48px', minWidth: '48px' }}
                        onClick={toggleChatCollapsed}
                        title="Expand AI Chat"
                    >
                        <div className="flex flex-col items-center gap-3 py-5">
                            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center shadow-lg shadow-violet-500/20 group-hover:scale-110 transition-transform">
                                <Sparkles className="w-4 h-4 text-white" />
                            </div>
                            <div className="flex flex-col items-center gap-1">
                                <span className="text-[10px] font-bold text-slate-400 tracking-widest uppercase" style={{ writingMode: 'vertical-lr' }}>AI Chat</span>
                            </div>
                        </div>
                        <div className="mt-auto mb-4 p-1.5 rounded-lg bg-slate-700/50 group-hover:bg-violet-600/30 transition-colors">
                            <PanelLeftOpen className="w-4 h-4 text-slate-400 group-hover:text-violet-300 transition-colors" />
                        </div>
                    </div>
                )}

                {/* Chat Panel */}
                <div
                    className={`flex-col bg-slate-800 ${mobileActivePanel === 'chat' ? 'flex' : 'hidden lg:flex'}`}
                    style={{
                        width: window.innerWidth >= 1024
                            ? (isChatCollapsed ? '0px' : `${splitRatio}%`)
                            : '100%',
                        minWidth: window.innerWidth >= 1024 && isChatCollapsed ? '0px' : undefined,
                        overflow: isChatCollapsed && window.innerWidth >= 1024 ? 'hidden' : undefined,
                        opacity: isChatCollapsed && window.innerWidth >= 1024 ? 0 : 1,
                        transition: 'width 300ms ease, opacity 200ms ease, min-width 300ms ease',
                        pointerEvents: isChatCollapsed && window.innerWidth >= 1024 ? 'none' : undefined
                    }}
                >

                    {/* Chat Messages */}
                    <div className="flex-1 overflow-y-auto overscroll-contain px-4 py-4 md:px-5 md:py-6 space-y-6" style={{ WebkitOverflowScrolling: 'touch' }}>
                        {messages.map((message) => (
                            <div
                                key={message.id}
                                className={`flex gap-3 ${message.role === 'user' ? 'justify-end' : ''}`}
                            >
                                {message.role === 'assistant' && (
                                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center shadow-lg shadow-violet-500/20">
                                        <Sparkles className="w-4 h-4 text-white" />
                                    </div>
                                )}

                                <div className={`flex flex-col max-w-[85%] ${message.role === 'user' ? 'items-end' : 'items-start'}`}>
                                    <div
                                        className={`rounded-2xl px-4 py-3 shadow-sm ${message.role === 'user'
                                            ? 'bg-violet-600 text-white rounded-br-sm'
                                            : 'bg-slate-700 text-slate-100 rounded-bl-sm'
                                            }`}
                                    >
                                        {message.role === 'user' ? (
                                            <p className="text-[15px] leading-relaxed whitespace-pre-wrap">{message.content}</p>
                                        ) : (
                                            <div className="space-y-3">
                                                {/* Action Cards - Show what AI did */}
                                                {message.action && ['generated', 'updated', 'file_attached', 'file_processed'].includes(message.action) && (
                                                    <div className="space-y-1.5 mb-2">
                                                        {message.action === 'generated' && (
                                                            <div className="flex items-center gap-2 bg-slate-800/50 px-3 py-2 rounded-lg border border-slate-600/30">
                                                                <FileText className="w-4 h-4 text-violet-400" />
                                                                <span className="text-sm text-slate-300">Created:</span>
                                                                <span className="text-sm font-medium text-white">
                                                                    {formData.task_type}/{formData.title || 'New Task'}
                                                                </span>
                                                            </div>
                                                        )}
                                                        {message.action === 'updated' && (
                                                            <div className="flex items-center gap-2 bg-slate-800/50 px-3 py-2 rounded-lg border border-slate-600/30">
                                                                <FileText className="w-4 h-4 text-blue-400" />
                                                                <span className="text-sm text-slate-300">Edited:</span>
                                                                <span className="text-sm font-medium text-white">
                                                                    {formData.task_type}/{formData.title || 'Task'}
                                                                </span>
                                                            </div>
                                                        )}
                                                        {message.action === 'file_attached' && (
                                                            <div className="flex items-center gap-2 bg-slate-800/50 px-3 py-2 rounded-lg border border-slate-600/30">
                                                                <Paperclip className="w-4 h-4 text-emerald-400" />
                                                                <span className="text-sm text-slate-300">Attached:</span>
                                                                <span className="text-sm font-medium text-white">File to Task</span>
                                                            </div>
                                                        )}
                                                        {message.action === 'file_processed' && (
                                                            <div className="flex items-center gap-2 bg-slate-800/50 px-3 py-2 rounded-lg border border-slate-600/30">
                                                                <Brain className="w-4 h-4 text-pink-400" />
                                                                <span className="text-sm text-slate-300">Analyzed:</span>
                                                                <span className="text-sm font-medium text-white">Document content</span>
                                                            </div>
                                                        )}
                                                    </div>
                                                )}

                                                {/* Message Content */}
                                                <div className="text-[15px] leading-relaxed">
                                                    {message.content.split('\n').map((line, i) => (
                                                        <p key={i} className="mb-1.5 last:mb-0">
                                                            {line.startsWith('**') && line.endsWith('**') ? (
                                                                <strong className="text-white font-semibold">{line.slice(2, -2)}</strong>
                                                            ) : line.startsWith('- ') ? (
                                                                <span className="flex gap-2">
                                                                    <span className="text-violet-400 font-bold">•</span>
                                                                    <span>{line.slice(2)}</span>
                                                                </span>
                                                            ) : line}
                                                        </p>
                                                    ))}
                                                </div>

                                                {/* Web Search Results */}
                                                {message.webSearchResults && message.webSearchResults.length > 0 && (
                                                    <WebSearchResultsDisplay results={message.webSearchResults} />
                                                )}

                                                {/* Intent Chooser Buttons */}
                                                {message.action === 'file_intent_choice' && message.pendingFiles && (
                                                    <div className="mt-3 pt-3 border-t border-slate-600/30">
                                                        <div className="flex flex-wrap gap-2">
                                                            <button
                                                                onClick={() => handleAttachToTask(message.pendingFiles)}
                                                                className="flex items-center gap-2 px-3 py-1.5 bg-slate-800 hover:bg-slate-900 text-slate-200 rounded-lg text-xs transition-colors border border-slate-600"
                                                            >
                                                                <Paperclip className="w-3.5 h-3.5" />
                                                                <span>Attach to Task</span>
                                                            </button>
                                                            <button
                                                                onClick={() => handleLetAIAnalyze(message.pendingFiles)}
                                                                className="flex items-center gap-2 px-3 py-1.5 bg-violet-600 hover:bg-violet-700 text-white rounded-lg text-xs transition-colors shadow-lg shadow-violet-500/20"
                                                            >
                                                                <Brain className="w-3.5 h-3.5" />
                                                                <span>Analyze Content</span>
                                                            </button>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                    <p className="text-[10px] text-slate-500 mt-1 px-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                        {new Date(message.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                    </p>
                                </div>
                            </div>
                        ))}

                        {/* Streaming/Thinking Indicator (Problem 4: Streaming Response) */}
                        {(isGenerating || isStreaming) && (
                            <div className="space-y-3">
                                {/* Phase Indicator */}
                                {streamingPhase && (
                                    <div className="flex items-center gap-2 text-violet-300">
                                        <Loader className="w-4 h-4 animate-spin" />
                                        <span className="text-sm font-medium">{streamingPhase}</span>
                                    </div>
                                )}

                                {/* Streaming Message Content */}
                                {streamingMessage ? (
                                    <div className="flex gap-3">
                                        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center shadow-lg shadow-violet-500/20">
                                            <Sparkles className="w-4 h-4 text-white" />
                                        </div>
                                        <div className="rounded-2xl px-4 py-3 shadow-sm bg-slate-700 text-slate-100 rounded-bl-sm">
                                            <div className="text-[15px] leading-relaxed">
                                                {streamingMessage}
                                                <span className="inline-block w-1.5 h-4 bg-violet-400 animate-pulse ml-1 align-text-bottom" />
                                            </div>
                                        </div>
                                    </div>
                                ) : !streamingPhase && (
                                    <div className="flex items-center gap-2 text-slate-400 ml-11">
                                        <span className="text-sm">Thinking...</span>
                                        <span className="inline-block w-1.5 h-4 bg-slate-500 animate-pulse" />
                                    </div>
                                )}
                            </div>
                        )}

                        <div ref={chatEndRef} />
                    </div>

                    {/* Suggestions */}
                    {suggestions.length > 0 && !isGenerating && (
                        <div className="px-4 pb-3 md:px-5">
                            <div className="flex items-center gap-2 mb-2 px-1">
                                <Lightbulb className="w-3 h-3 text-violet-400" />
                                <span className="text-xs font-medium text-slate-400">Suggestions</span>
                            </div>
                            <div className="flex gap-2 overflow-x-auto whitespace-nowrap scrollbar-hide pb-2 px-1">
                                {suggestions.map((suggestion, index) => (
                                    <button
                                        key={index}
                                        onClick={() => handleSuggestionClick(suggestion)}
                                        className="text-xs text-slate-300 hover:text-white bg-slate-700 hover:bg-slate-600 border border-slate-600 hover:border-slate-500 px-3 py-2 rounded-full transition-all flex-shrink-0"
                                    >
                                        {suggestion}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* File Processing Indicator */}
                    {fileProcessingMessage && (
                        <div className="px-5 pb-2">
                            <div className="flex items-center gap-2 text-sm text-violet-400">
                                <Loader className="w-3.5 h-3.5 animate-spin" />
                                <span>{fileProcessingMessage}</span>
                            </div>
                        </div>
                    )}

                    {/* Chat Input */}
                    <div className="p-4 md:p-5 border-t border-slate-800 bg-slate-900/50">
                        {/* Hidden File Input */}


                        {/* Inline File Upload Progress */}
                        {isFileUploading && (
                            <div className="mb-3 bg-slate-800 rounded-lg p-2.5 border border-slate-700">
                                <div className="flex items-center gap-2 mb-1.5">
                                    <Loader className="w-3.5 h-3.5 animate-spin text-violet-400" />
                                    <span className="text-xs text-slate-300">Uploading...</span>
                                    <span className="text-xs text-slate-500 ml-auto">{fileUploadProgress}%</span>
                                </div>
                                <div className="w-full bg-slate-700 rounded-full h-1 overflow-hidden">
                                    <div
                                        className="bg-violet-500 h-full rounded-full transition-all duration-300"
                                        style={{ width: `${fileUploadProgress}%` }}
                                    />
                                </div>
                            </div>
                        )}

                        {/* Uploaded Files Display */}
                        {aiUploadedFiles.length > 0 && (
                            <div className="flex flex-wrap gap-2 mb-3">
                                {aiUploadedFiles.map((file) => {
                                    const ext = file.file_name?.split('.').pop()?.toUpperCase() || 'FILE';
                                    const isPdf = ext === 'PDF';
                                    const isDoc = ['DOC', 'DOCX'].includes(ext);
                                    const isImage = ['JPG', 'JPEG', 'PNG', 'GIF', 'WEBP'].includes(ext);
                                    const isSpreadsheet = ['XLS', 'XLSX'].includes(ext);

                                    return (
                                        <div
                                            key={file.file_key}
                                            className="group flex items-center gap-2 bg-slate-800 hover:bg-slate-700 border border-slate-700 hover:border-slate-600 rounded-lg pl-2.5 pr-1.5 py-1.5 transition-colors"
                                        >
                                            <div className={`w-5 h-5 rounded flex items-center justify-center text-[10px] font-bold ${isPdf ? 'bg-red-500/20 text-red-400' :
                                                isDoc ? 'bg-blue-500/20 text-blue-400' :
                                                    isImage ? 'bg-green-500/20 text-green-400' :
                                                        isSpreadsheet ? 'bg-emerald-500/20 text-emerald-400' :
                                                            'bg-slate-600/50 text-slate-400'
                                                }`}>
                                                {isPdf ? 'PDF' : isDoc ? 'DOC' : isImage ? 'IMG' : isSpreadsheet ? 'XLS' : ext.slice(0, 3)}
                                            </div>
                                            <span className="text-xs text-slate-300 max-w-[120px] truncate font-medium">
                                                {file.file_name}
                                            </span>
                                            <button
                                                onClick={() => handleRemoveAiFile(file)}
                                                className="p-1 text-slate-500 hover:text-red-400 rounded-md transition-colors opacity-60 group-hover:opacity-100"
                                                title="Remove file"
                                            >
                                                <X className="w-3.5 h-3.5" />
                                            </button>
                                        </div>
                                    );
                                })}
                            </div>
                        )}

                        {/* Input Wrapper */}
                        <div className="relative">
                            <textarea
                                ref={inputRef}
                                value={inputMessage}
                                onChange={(e) => setInputMessage(e.target.value)}
                                onKeyPress={handleKeyPress}
                                placeholder="Describe the task you want to create..."
                                rows={2}
                                className="w-full px-4 py-3 pr-24 bg-slate-700 border border-slate-600 rounded-xl text-white placeholder-slate-400 focus:ring-2 focus:ring-violet-500 focus:border-transparent resize-none"
                            />

                            <div className="absolute right-2 bottom-2 flex gap-1">
                                <button
                                    onClick={() => {
                                        setPickerMode('chat');
                                        setShowFilePicker(true);
                                    }}
                                    disabled={isGenerating || isFileUploading || aiUploadedFiles.length >= 5}
                                    className={`p-1.5 rounded-lg transition-colors ${aiUploadedFiles.length > 0
                                        ? 'bg-violet-600 text-white'
                                        : 'text-slate-400 hover:text-white hover:bg-slate-600'
                                        } disabled:opacity-50 disabled:cursor-not-allowed`}
                                    title="Attach files"
                                >
                                    <Paperclip className="w-4 h-4" />
                                </button>

                                <button
                                    onClick={handleSendMessage}
                                    disabled={!inputMessage.trim() || isGenerating}
                                    className="p-1.5 bg-violet-600 hover:bg-violet-500 text-white rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-violet-500/20"
                                >
                                    {isGenerating ? (
                                        <Loader className="w-4 h-4 animate-spin" />
                                    ) : (
                                        <Send className="w-4 h-4" />
                                    )}
                                </button>
                            </div>
                        </div>
                        <div className="text-center mt-2">
                            <p className="text-[10px] text-slate-600">
                                AI can make mistakes. Please review generated tasks.
                            </p>
                        </div>
                    </div>
                </div>

                {/* Draggable Divider - hidden when chat is collapsed */}
                {!isChatCollapsed && (
                    <div
                        className="hidden lg:flex flex-col items-center justify-center w-1 bg-slate-800 hover:bg-violet-600/20 cursor-col-resize transition-colors group z-20"
                        onMouseDown={handleMouseDown}
                    >
                        <div className="absolute flex flex-col items-center justify-center w-4 h-12 rounded bg-slate-700 group-hover:bg-violet-600 transition-colors shadow-lg border border-slate-600 group-hover:border-violet-500">
                            <GripVertical className="w-3 h-3 text-slate-400 group-hover:text-white" />
                        </div>
                    </div>
                )}

                {/* Right Panel - 3-Panel Task Editor */}
                <div
                    className={`flex flex-col bg-white dark:bg-slate-900 overflow-hidden ${mobileActivePanel === 'editor' ? 'flex' : 'hidden lg:flex'} relative transition-all duration-300`}
                    style={{
                        width: window.innerWidth >= 1024
                            ? (isChatCollapsed ? '100%' : `${100 - splitRatio}%`)
                            : '100%',
                        flex: isChatCollapsed && window.innerWidth >= 1024 ? '1 1 0%' : undefined,
                        transition: 'width 300ms ease, flex 300ms ease'
                    }}
                >
                    {/* AI Loading Overlay */}
                    {(isGenerating || isStreaming) && (
                        <div className="absolute inset-0 z-50 bg-white/80 dark:bg-slate-900/80 backdrop-blur-[2px] flex flex-col items-center justify-center animate-in fade-in duration-300">
                            <div className="bg-white dark:bg-slate-900 p-6 rounded-2xl shadow-xl border border-violet-100 dark:border-violet-500/20 flex flex-col items-center max-w-xs text-center">
                                <div className="relative w-12 h-12 mb-4">
                                    <div className="absolute inset-0 rounded-full border-4 border-violet-100 dark:border-violet-500/20"></div>
                                    <div className="absolute inset-0 rounded-full border-4 border-violet-500 border-t-transparent animate-spin"></div>
                                    <div className="absolute inset-0 flex items-center justify-center">
                                        <Sparkles className="w-5 h-5 text-violet-600 animate-pulse" />
                                    </div>
                                </div>
                                <h3 className="text-base font-bold text-gray-800 dark:text-slate-100 mb-1">{streamingPhase || 'Generating Task...'}</h3>
                                <p className="text-gray-500 dark:text-slate-400 text-xs">AI is crafting your content.</p>
                            </div>
                        </div>
                    )}

                    {/* ─── Editor Top Bar ─── */}
                    <div className="flex items-center gap-4 px-5 py-3 border-b border-gray-200 dark:border-slate-700/60 bg-white dark:bg-slate-900 flex-shrink-0">
                        {/* Left: Back + Title + Status */}
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                            {!isFirstStep && (
                                <button
                                    onClick={handlePrevStep}
                                    className="flex items-center gap-1 px-2 py-1.5 text-sm font-medium text-gray-600 dark:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-800 rounded-lg transition-colors flex-shrink-0"
                                >
                                    <ChevronLeft className="w-4 h-4" />
                                    Back
                                </button>
                            )}
                            <input
                                type="text"
                                name="title"
                                value={formData.title}
                                onChange={handleChange}
                                placeholder="Untitled Task"
                                className="flex-1 min-w-0 text-lg font-semibold text-gray-900 dark:text-white placeholder-gray-300 dark:placeholder-slate-600 bg-transparent border-none outline-none focus:ring-0 p-0"
                            />
                            <div className="flex items-center gap-2 flex-shrink-0">
                                <span className="text-xs font-medium text-gray-400 dark:text-slate-500 bg-gray-100 dark:bg-slate-800 px-2.5 py-1 rounded-md">
                                    {activeEditorTab === 'content' ? 'Draft' : activeEditorTab === 'settings' ? 'Step 2/3 — Settings' : 'Step 3/3 — Assignment'}
                                </span>
                                {/* Auto-save status indicator */}
                                {autoSaveStatus === 'saving' && (
                                    <span className="flex items-center gap-1.5 text-xs font-medium text-blue-500 dark:text-blue-400 animate-pulse">
                                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                        Saving...
                                    </span>
                                )}
                                {autoSaveStatus === 'saved' && (
                                    <span className="flex items-center gap-1.5 text-xs font-medium text-emerald-600 dark:text-emerald-400">
                                        <Check className="w-3.5 h-3.5" />
                                        Draft saved
                                    </span>
                                )}
                                {autoSaveStatus === 'error' && (
                                    <span className="flex items-center gap-1.5 text-xs font-medium text-red-500 dark:text-red-400">
                                        <AlertCircle className="w-3.5 h-3.5" />
                                        Save failed
                                    </span>
                                )}
                            </div>
                        </div>
                        {/* Right: Actions */}
                        <div className="flex items-center gap-2 flex-shrink-0">
                            <button
                                onClick={() => setIsPreviewOpen(true)}
                                disabled={!formData.title?.trim() && (!formData.sections || formData.sections.length === 0)}
                                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 dark:text-slate-200 border border-gray-300 dark:border-slate-600 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors disabled:opacity-40"
                            >
                                <Eye className="w-4 h-4" />
                                Preview
                            </button>
                            {activeEditorTab === 'assignment' ? (
                                <button
                                    onClick={handlePublish}
                                    disabled={isSaving || !formData.title?.trim()}
                                    className="flex items-center gap-2 px-5 py-2 text-sm font-semibold bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-40 shadow-sm"
                                >
                                    <Send className="w-4 h-4" />
                                    Publish
                                </button>
                            ) : (
                                <button
                                    onClick={handleNextStep}
                                    className="flex items-center gap-2 px-5 py-2 text-sm font-semibold bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors shadow-sm"
                                >
                                    Next
                                    <ChevronRight className="w-4 h-4" />
                                </button>
                            )}
                        </div>
                    </div>

                    {/* ─── 3-Panel Editor Body (Content tab) / Full page (Settings/Assignment) ─── */}
                    {activeEditorTab === 'content' ? (
                        <div className="flex-1 flex overflow-hidden">

                            {/* ── Left: Blocks Sidebar ── */}
                            <div className="w-[220px] min-w-[200px] border-r border-gray-200 dark:border-slate-700/60 bg-white dark:bg-slate-900 flex flex-col overflow-hidden flex-shrink-0">
                                <div className="px-4 pt-4 pb-2">
                                    <h3 className="text-base font-bold text-gray-900 dark:text-white mb-3">Blocks</h3>
                                    <div className="relative">
                                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-slate-500" />
                                        <input
                                            type="text"
                                            value={blockSearchQuery}
                                            onChange={(e) => setBlockSearchQuery(e.target.value)}
                                            placeholder="Search blocks..."
                                            className="w-full pl-9 pr-3 py-2.5 text-sm bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg text-gray-700 dark:text-slate-300 placeholder-gray-400 dark:placeholder-slate-500 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                                        />
                                    </div>
                                </div>
                                <div className="flex-1 overflow-y-auto px-3 pb-3 space-y-4">
                                    {getFilteredBlocks().map(category => (
                                        <div key={category.category}>
                                            <p className="text-[11px] font-bold text-gray-400 dark:text-slate-500 uppercase tracking-wider px-1 mb-2">{category.category}</p>
                                            <div className="space-y-1">
                                                {category.items.map(block => {
                                                    const BlockIcon = block.icon;
                                                    return (
                                                        <button
                                                            key={block.type}
                                                            onClick={() => addBlockFromSidebar(block.type)}
                                                            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left hover:bg-gray-50 dark:hover:bg-slate-800 border border-transparent hover:border-gray-200 dark:hover:border-slate-700 transition-all group"
                                                        >
                                                            <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${getBlockBgColor(block.type)} group-hover:scale-105 transition-transform`}>
                                                                <BlockIcon className={`w-4 h-4 ${getBlockColor(block.type)}`} />
                                                            </div>
                                                            <div className="min-w-0">
                                                                <p className="text-sm font-medium text-gray-700 dark:text-slate-200 truncate">{block.label}</p>
                                                                <p className="text-xs text-gray-400 dark:text-slate-500 truncate">{block.desc}</p>
                                                            </div>
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* ── Center: Canvas ── */}
                            <div className="flex-1 overflow-y-auto bg-gray-50 dark:bg-slate-950/50 p-6" style={{ WebkitOverflowScrolling: 'touch' }}>
                                {/* Description */}
                                <div className="mb-5">
                                    <textarea
                                        name="description"
                                        value={formData.description}
                                        onChange={handleChange}
                                        placeholder="Add a task description..."
                                        rows="2"
                                        className="w-full px-4 py-3 text-sm bg-white dark:bg-slate-800/60 text-gray-700 dark:text-slate-300 placeholder-gray-400 dark:placeholder-slate-500 border border-gray-200 dark:border-slate-700/60 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                                    />
                                </div>

                                {/* Section Block Cards with Drag & Drop */}
                                {formData.sections.length === 0 ? (
                                    <div className="border-2 border-dashed border-gray-200 dark:border-slate-700 rounded-xl p-12 text-center">
                                        <Layers className="w-10 h-10 text-gray-300 dark:text-slate-600 mx-auto mb-3" />
                                        <p className="text-base text-gray-400 dark:text-slate-500 font-medium">No blocks yet</p>
                                        <p className="text-sm text-gray-400 dark:text-slate-600 mt-1">Click a block type from the sidebar to add</p>
                                    </div>
                                ) : (
                                    <DndContext sensors={dndSensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                                        <SortableContext items={formData.sections.map((s, i) => s.id || `section-${i}`)} strategy={verticalListSortingStrategy}>
                                            <div className="space-y-2.5">
                                                {formData.sections.map((section, index) => (
                                                    <SortableBlockCard
                                                        key={section.id || `section-${index}`}
                                                        section={section}
                                                        index={index}
                                                        isSelected={selectedSectionIndex === index}
                                                        onSelect={(i) => { setSelectedSectionIndex(i); setRightPanelTab('section'); }}
                                                        onDelete={deleteSectionFromCanvas}
                                                        getBlockIcon={getBlockIcon}
                                                        getBlockColor={getBlockColor}
                                                        getBlockBgColor={getBlockBgColor}
                                                        getSectionSummary={getSectionSummary}
                                                        sectionsLength={formData.sections.length}
                                                    />
                                                ))}
                                            </div>
                                        </SortableContext>
                                    </DndContext>
                                )}

                                {/* Drop hint */}
                                <div className="mt-4 border-2 border-dashed border-gray-200 dark:border-slate-700 rounded-xl p-4 text-center">
                                    <p className="text-sm text-gray-400 dark:text-slate-500">Drag more blocks here or click a block type to add</p>
                                </div>

                                {/* File Attachments */}
                                <div className="mt-8 pt-5 border-t border-gray-200 dark:border-slate-700/40">
                                    <div className="flex items-center justify-between mb-3">
                                        <span className="text-sm font-semibold text-gray-700 dark:text-slate-300">Attachments</span>
                                        <button
                                            type="button"
                                            onClick={() => { setPickerMode('editor'); setShowFilePicker(true); }}
                                            disabled={formData.attachments.length >= 5}
                                            className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 font-medium disabled:opacity-40"
                                        >
                                            + Add File
                                        </button>
                                    </div>
                                    {formData.attachments.length > 0 && (
                                        <div className="space-y-1.5">
                                            {formData.attachments.map((file, index) => (
                                                <div key={index} className="flex items-center gap-3 px-3 py-2 bg-white dark:bg-slate-800/60 border border-gray-200 dark:border-slate-700/60 rounded-lg">
                                                    <FileText className="w-4 h-4 text-gray-400 flex-shrink-0" />
                                                    <span className="text-sm text-gray-700 dark:text-slate-300 truncate flex-1">{file.file_name}</span>
                                                    <button onClick={() => handleRemoveAttachment(file)} className="text-gray-400 hover:text-red-500 flex-shrink-0"><X className="w-4 h-4" /></button>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* ── Right: Settings Panel ── */}
                            <div className="w-[320px] lg:w-[350px] min-w-[300px] border-l border-gray-200 dark:border-slate-700/60 bg-white dark:bg-slate-900 flex flex-col overflow-hidden flex-shrink-0">
                                {selectedSectionIndex !== null && formData.sections[selectedSectionIndex] ? (() => {
                                    const section = formData.sections[selectedSectionIndex];
                                    const BlockIcon = getBlockIcon(section.section_type);
                                    return (
                                        <>
                                            {/* Panel Header */}
                                            <div className="flex items-center gap-3 px-4 py-3.5 border-b border-gray-200 dark:border-slate-700/60 flex-shrink-0">
                                                <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${getBlockBgColor(section.section_type)}`}>
                                                    <BlockIcon className={`w-4 h-4 ${getBlockColor(section.section_type)}`} />
                                                </div>
                                                <span className="text-base font-semibold text-gray-900 dark:text-white capitalize flex-1">{section.section_type.replace('_', ' ')}</span>
                                                <button onClick={() => setSelectedSectionIndex(null)} className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-slate-300 rounded-md hover:bg-gray-100 dark:hover:bg-slate-800">
                                                    <X className="w-4 h-4" />
                                                </button>
                                            </div>

                                            {/* Panel Content */}
                                            <div className="flex-1 overflow-y-auto p-4 space-y-5">
                                                {/* Block Title */}
                                                <div>
                                                    <label className="block text-sm font-semibold text-gray-700 dark:text-slate-200 mb-2">Block Title</label>
                                                    <input
                                                        type="text"
                                                        value={section.title}
                                                        onChange={(e) => {
                                                            const updated = [...formData.sections];
                                                            updated[selectedSectionIndex] = { ...updated[selectedSectionIndex], title: e.target.value };
                                                            setFormData(prev => ({ ...prev, sections: updated }));
                                                        }}
                                                        className="w-full px-3 py-2.5 text-sm bg-gray-50 dark:bg-slate-800 text-gray-900 dark:text-white border border-gray-200 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                    />
                                                </div>

                                                {/* Required Block Toggle */}
                                                <div className={`rounded-xl border-2 p-4 transition-colors ${section.is_required
                                                    ? 'border-amber-200 dark:border-amber-500/30 bg-amber-50/50 dark:bg-amber-500/5'
                                                    : 'border-gray-200 dark:border-slate-700'
                                                    }`}>
                                                    <div className="flex items-center justify-between">
                                                        <div>
                                                            <p className={`text-sm font-semibold ${section.is_required ? 'text-amber-700 dark:text-amber-300' : 'text-gray-700 dark:text-slate-200'}`}>Required Block</p>
                                                            <p className="text-xs text-gray-400 dark:text-slate-500 mt-0.5">Must be completed to submit task</p>
                                                        </div>
                                                        <button
                                                            onClick={() => {
                                                                const updated = [...formData.sections];
                                                                updated[selectedSectionIndex] = { ...updated[selectedSectionIndex], is_required: !section.is_required };
                                                                setFormData(prev => ({ ...prev, sections: updated }));
                                                            }}
                                                            className={`relative w-10 h-6 rounded-full transition-colors flex-shrink-0 ${section.is_required ? 'bg-blue-600' : 'bg-gray-300 dark:bg-slate-600'}`}
                                                        >
                                                            <div className={`absolute top-1 w-4 h-4 rounded-full bg-white shadow-sm transition-transform ${section.is_required ? 'translate-x-5' : 'translate-x-1'}`} />
                                                        </button>
                                                    </div>
                                                </div>

                                                {/* Quiz-specific settings */}
                                                {section.section_type === 'quiz' && (
                                                    <>
                                                        <div>
                                                            <label className="block text-sm font-semibold text-gray-700 dark:text-slate-200 mb-2">Grading Type</label>
                                                            <select
                                                                value={section.grading_type || 'auto'}
                                                                onChange={(e) => {
                                                                    const updated = [...formData.sections];
                                                                    updated[selectedSectionIndex] = { ...updated[selectedSectionIndex], grading_type: e.target.value };
                                                                    setFormData(prev => ({ ...prev, sections: updated }));
                                                                }}
                                                                className="w-full px-3 py-2.5 text-sm bg-gray-50 dark:bg-slate-800 text-gray-900 dark:text-white border border-gray-200 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-blue-500"
                                                            >
                                                                <option value="auto">Auto-Graded</option>
                                                                <option value="manual">Manual Review</option>
                                                            </select>
                                                        </div>
                                                        <div>
                                                            <label className="block text-sm font-semibold text-gray-700 dark:text-slate-200 mb-2">Passing Score (%)</label>
                                                            <input
                                                                type="number"
                                                                value={section.passing_score || 70}
                                                                onChange={(e) => {
                                                                    const updated = [...formData.sections];
                                                                    updated[selectedSectionIndex] = { ...updated[selectedSectionIndex], passing_score: parseInt(e.target.value) };
                                                                    setFormData(prev => ({ ...prev, sections: updated }));
                                                                }}
                                                                min="0" max="100"
                                                                className="w-full px-3 py-2.5 text-sm bg-gray-50 dark:bg-slate-800 text-gray-900 dark:text-white border border-gray-200 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-blue-500"
                                                            />
                                                        </div>
                                                    </>
                                                )}

                                                {/* Content Editor */}
                                                <div className="pt-3 border-t border-gray-100 dark:border-slate-700/40">
                                                    <p className="text-sm font-semibold text-gray-700 dark:text-slate-200 mb-3">Content</p>
                                                    <CombinedTaskBuilder
                                                        sections={formData.sections}
                                                        onChange={(sections) => setFormData(prev => ({ ...prev, sections }))}
                                                        onContentChange={trackContentChange}
                                                        focusedIndex={selectedSectionIndex}
                                                    />
                                                </div>
                                            </div>
                                        </>
                                    );
                                })() : (
                                    <div className="flex-1 flex flex-col items-center justify-center p-6 text-center">
                                        <div className="w-12 h-12 rounded-xl bg-gray-100 dark:bg-slate-800 flex items-center justify-center mb-3">
                                            <MousePointer className="w-5 h-5 text-gray-400 dark:text-slate-500" />
                                        </div>
                                        <p className="text-sm font-medium text-gray-500 dark:text-slate-400">No block selected</p>
                                        <p className="text-xs text-gray-400 dark:text-slate-500 mt-1">Click a block in the canvas to edit its settings</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    ) : (
                        /* ─── Full-page Settings / Assignment view with footer nav ─── */
                        <>
                            <div className="flex-1 overflow-y-auto p-6 bg-gray-50 dark:bg-slate-900" style={{ WebkitOverflowScrolling: 'touch' }}>
                                {activeEditorTab === 'settings' && (
                                    <div className="max-w-2xl mx-auto space-y-6">
                                        <div className="bg-white dark:bg-slate-800/60 rounded-xl border border-gray-200 dark:border-slate-700/80 p-5 shadow-sm">
                                            <h3 className="text-base font-semibold text-gray-800 dark:text-slate-100 mb-4">General Settings</h3>
                                            <div className="grid grid-cols-2 gap-4">
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-600 dark:text-slate-300 mb-1.5">Category</label>
                                                    <input type="text" name="category" value={formData.category} onChange={handleChange} placeholder="e.g., Compliance, IT Setup" className="w-full px-3 py-2.5 bg-gray-50 dark:bg-slate-900/60 text-gray-900 dark:text-white border border-gray-200 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm" />
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-600 dark:text-slate-300 mb-1.5">Priority</label>
                                                    <select name="priority" value={formData.priority} onChange={handleChange} className="w-full px-3 py-2.5 bg-gray-50 dark:bg-slate-900/60 text-gray-900 dark:text-white border border-gray-200 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm">
                                                        <option value="low">Low</option>
                                                        <option value="medium">Medium</option>
                                                        <option value="high">High</option>
                                                    </select>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="bg-white dark:bg-slate-800/60 rounded-xl border border-gray-200 dark:border-slate-700/80 p-5 shadow-sm">
                                            <h3 className="text-base font-semibold text-gray-800 dark:text-slate-100 mb-4">Timing & Duration</h3>
                                            <div className="grid grid-cols-2 gap-4">
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-600 dark:text-slate-300 mb-1.5">Duration (minutes)</label>
                                                    <input type="number" name="estimated_duration" value={formData.estimated_duration} onChange={handleChange} min="5" className="w-full px-3 py-2.5 bg-gray-50 dark:bg-slate-900/60 text-gray-900 dark:text-white border border-gray-200 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm" />
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-600 dark:text-slate-300 mb-1.5">Due Days After Assignment</label>
                                                    <input type="number" name="due_days_after_assignment" value={formData.due_days_after_assignment} onChange={handleChange} min="1" className="w-full px-3 py-2.5 bg-gray-50 dark:bg-slate-900/60 text-gray-900 dark:text-white border border-gray-200 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm" />
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-600 dark:text-slate-300 mb-1.5">Schedule Date <span className="text-gray-400 font-normal">(Optional)</span></label>
                                                    <input type="datetime-local" name="scheduled_date" value={formData.scheduled_date || ''} onChange={handleChange} className="w-full px-3 py-2.5 bg-gray-50 dark:bg-slate-900/60 text-gray-900 dark:text-white border border-gray-200 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm" />
                                                </div>
                                                {formData.task_type === 'quiz' && (
                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-600 dark:text-slate-300 mb-1.5">Passing Score (%)</label>
                                                        <input type="number" name="passing_score" value={formData.passing_score} onChange={handleChange} min="0" max="100" className="w-full px-3 py-2.5 bg-gray-50 dark:bg-slate-900/60 text-gray-900 dark:text-white border border-gray-200 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm" />
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        <div className={`rounded-xl border-2 p-5 transition-all ${formData.is_compliance_required ? 'bg-red-50/50 dark:bg-red-500/5 border-red-200 dark:border-red-500/30' : 'bg-white dark:bg-slate-800/60 border-gray-200 dark:border-slate-700/80'}`}>
                                            <label className="flex items-center gap-3 cursor-pointer">
                                                <input type="checkbox" name="is_compliance_required" checked={formData.is_compliance_required} onChange={handleChange} disabled={isComplianceMode} className="w-4 h-4 rounded text-red-600 focus:ring-red-500" />
                                                <div>
                                                    <span className="text-sm font-semibold text-gray-800 dark:text-slate-100">Compliance-required task</span>
                                                    <p className="text-xs text-gray-500 dark:text-slate-400 mt-0.5">Tracked separately. Overdue tasks create violations.</p>
                                                </div>
                                            </label>
                                        </div>
                                    </div>
                                )}

                                {activeEditorTab === 'assignment' && (
                                    <div className="max-w-2xl mx-auto space-y-6">
                                        <div className="bg-white dark:bg-slate-800/60 rounded-xl border border-gray-200 dark:border-slate-700/80 p-5 shadow-sm">
                                            <h3 className="text-base font-semibold text-gray-800 dark:text-slate-100 mb-4">Assign Employees</h3>
                                            <EmployeeSelector
                                                phase={formData.phase}
                                                selectedEmployees={formData.assigned_employee_ids}
                                                onSelectionChange={(ids) => setFormData(prev => ({ ...prev, assigned_employee_ids: ids }))}
                                                assignmentType={formData.assignment_type}
                                                onAssignmentTypeChange={(type) => setFormData(prev => ({ ...prev, assignment_type: type }))}
                                                selectedTeams={formData.assigned_team_ids}
                                                onTeamSelectionChange={(ids) => setFormData(prev => ({ ...prev, assigned_team_ids: ids }))}
                                                assignmentMetadata={formData.assignment_metadata}
                                                onMetadataChange={(meta) => setFormData(prev => ({ ...prev, assignment_metadata: meta }))}
                                            />
                                        </div>
                                        {formData.assignment_type === 'role' && (
                                            <div className="bg-white dark:bg-slate-800/60 rounded-xl border border-gray-200 dark:border-slate-700/80 p-5 shadow-sm">
                                                <h3 className="text-base font-semibold text-gray-800 dark:text-slate-100 mb-3">Role Filter</h3>
                                                <select multiple value={formData.assigned_roles} onChange={handleRolesChange} className="w-full px-3 py-2.5 bg-gray-50 dark:bg-slate-900/60 border border-gray-200 dark:border-slate-600 rounded-lg text-sm text-gray-900 dark:text-white" size="4">
                                                    <option value="">All Roles</option>
                                                    {availableRoles.map((role) => (<option key={role.id} value={role.role_name}>{role.role_name}</option>))}
                                                </select>
                                            </div>
                                        )}
                                        <div className="bg-white dark:bg-slate-800/60 rounded-xl border border-gray-200 dark:border-slate-700/80 p-5 shadow-sm">
                                            <AssignmentPlaceholderPicker value={formData.assignment_placeholders || []} onChange={(val) => setFormData(prev => ({ ...prev, assignment_placeholders: val }))} />
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Footer Navigation */}
                            <div className="border-t border-gray-200 dark:border-slate-700/60 bg-white dark:bg-slate-900 px-6 py-3 flex items-center justify-between flex-shrink-0">
                                <button
                                    onClick={handlePrevStep}
                                    className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 dark:text-slate-200 border border-gray-300 dark:border-slate-600 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors"
                                >
                                    <ChevronLeft className="w-4 h-4" />
                                    Back
                                </button>
                                <span className="text-sm text-gray-400 dark:text-slate-500">
                                    {activeEditorTab === 'settings' ? 'Step 2 of 3 — Settings' : 'Step 3 of 3 — Assignment'}
                                </span>
                                {!isLastStep ? (
                                    <button
                                        onClick={handleNextStep}
                                        className="flex items-center gap-2 px-4 py-2 text-sm font-medium bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                                    >
                                        Next
                                        <ChevronRight className="w-4 h-4" />
                                    </button>
                                ) : (
                                    <div className="flex items-center gap-2">
                                        {formData.scheduled_date && (
                                            <button onClick={handleSchedule} disabled={isSaving || !formData.title?.trim()} className="flex items-center gap-2 px-4 py-2 text-sm font-medium bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-40">
                                                <Clock className="w-4 h-4" /> Schedule
                                            </button>
                                        )}
                                        <button onClick={handlePublish} disabled={isSaving || !formData.title?.trim()} className="flex items-center gap-2 px-4 py-2 text-sm font-semibold bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-40 shadow-sm">
                                            <Send className="w-4 h-4" /> Publish
                                        </button>
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                </div>

                {/* Auto-save loading bar */}
                {autoSaveStatus === 'saving' && (
                    <div className="fixed top-0 left-0 right-0 z-[60] h-1">
                        <div className="h-full bg-gradient-to-r from-violet-500 via-blue-500 to-violet-500 animate-pulse rounded-full" style={{ animation: 'autosave-bar 1.5s ease-in-out infinite' }} />
                    </div>
                )}
            </div>

            {/* Unified File Picker Modal */}
            <UnifiedFilePickerModal
                isOpen={showFilePicker}
                onClose={() => setShowFilePicker(false)}
                onAttach={handleFileSelection}
                mode={pickerMode === 'chat' ? 'all' : 'all'}
            />

            {/* Admin Task Preview Modal */}
            <AdminTaskPreviewModal
                isOpen={isPreviewOpen}
                onClose={() => setIsPreviewOpen(false)}
                taskData={formData}
            />

            {/* Document Prompt Modal */}
            <DocumentPromptModal
                isOpen={!!showDocumentPromptModal}
                onClose={() => {
                    const chatFiles = showDocumentPromptModal.isChat ? showDocumentPromptModal.files : null;
                    handleDocumentPromptConfirm(false, chatFiles);
                }}
                onConfirm={(addToDocs) => {
                    const chatFiles = showDocumentPromptModal.isChat ? showDocumentPromptModal.files : null;
                    handleDocumentPromptConfirm(addToDocs, chatFiles);
                }}
                fileCount={pendingDocumentPromptFiles.length}
            />
        </div >
    );
};

export default AITaskGenerator;

